import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaUsers,
  FaDownload,
  FaSync,
  FaChevronLeft,
  FaChevronRight,
  FaBuilding,
  FaProjectDiagram,
  FaSearch,
  FaTimes,
  FaUserTie,
  FaChevronDown,
  FaChevronUp,
  FaEye,
  FaMapMarkerAlt,
  FaChartBar,
  FaChartLine,
  FaExclamationTriangle,
  FaEdit,
  FaClipboardList,
  FaTrashAlt,
  FaUserMinus,
  FaExclamationCircle,
  FaFlagCheckered,
  FaUserClock,
  FaCircle,
  FaFolderOpen
} from 'react-icons/fa';
import { collection, getDocs, doc, query, where, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './SMOLeadReports.module.css';
import {
  isArchivedAssignment,
  getArchivedAssignmentLabel,
  formatUnassignedDate,
} from '../utils/assignmentHistory';
import { matchesSearchTerm } from '../utils/searchText';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useNavigate } from 'react-router-dom';
import ProjectVisitsModal from './ProfilePage/ProjectVisitsModal';
import ManualDataEntryModal from './SMOLeadReports/ManualDataEntryModal';
import IssueDetailModal from './SMOLeadReports/IssueDetailModal';
import EmployeeDocumentsModal from './SMOLeadReports/EmployeeDocumentsModal';
import TeamActivityPanel from './SMOLeadReports/TeamActivityPanel';
import { useTeamActivity } from './SMOLeadReports/useTeamActivity';
import { useTheme } from '../contexts/ThemeContext';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
  LineChart, Line
} from 'recharts';

const SMOLeadReports = () => {
  const { employeeId, employeeData, userAuthorization } = useUserProfile();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  
  // SMO list state
  const [smoList, setSmoList] = useState([]);
  const [selectedSMO, setSelectedSMO] = useState(null);
  const [loadingSMOs, setLoadingSMOs] = useState(true);

  // Delegated SMO views for users assigned as "backup" SMO Lead on a project.
  // Each entry mirrors the primer SMO Lead's view but restricted to that project.
  const [delegatedSmoViews, setDelegatedSmoViews] = useState([]);

  // Delegated SMO views for users set as the "Yedek SMO" (backup) of one or
  // more centers in Merkez Yönetimi. Each entry mirrors the center's primer SMO
  // Lead view, scoped to just the centers where this user is the backup.
  const [centerBackupViews, setCenterBackupViews] = useState([]);
  
  // Workers under selected SMO
  const [workers, setWorkers] = useState([]);
  // CV/GCP/IATA belgeleri görüntülenen çalışan (Çalışan Yönetimi'ndeki
  // "CV/GCP/IATA Belgeleri" bölümüyle aynı kaynak, salt okunur)
  const [documentsEmployee, setDocumentsEmployee] = useState(null);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  
  // Centers for the selected SMO
  const [smoCenters, setSmoCenters] = useState([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Status filter state
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive', 'incomplete'
  const [primerFilter, setPrimerFilter] = useState('all'); // 'all', 'primer', 'backup'
  const [blindedFilter, setBlindedFilter] = useState('all'); // 'all', 'blinded', 'unblinded'
  const [hideFinished, setHideFinished] = useState(false); // hide finished projects
  
  // Table scroll state
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  
  // Modal state
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showVisitsModal, setShowVisitsModal] = useState(false);

  // Expanded employee rows (to show visit summary inline)
  const [expandedWorkers, setExpandedWorkers] = useState({});

  // Collapsed cities state - tracks which cities are collapsed
  const [collapsedCities, setCollapsedCities] = useState({});

  // All visits for selected SMO's workers
  const [allVisits, setAllVisits] = useState([]);
  const [loadingVisits, setLoadingVisits] = useState(false);

  // All calendar jobs for selected SMO's workers
  const [allCalendarJobs, setAllCalendarJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  // Issue Detail modal — opened by clicking any "issue" indicator across the
  // dashboard sections. Shows what the issue was, who caused it and when.
  const [issueModal, setIssueModal] = useState(null); // { title, visits } | null

  // Collapsible dashboard sections — collapsed by default to avoid rendering heavy charts
  const [expandedSections, setExpandedSections] = useState({
    issueTracking: false,
    hastaneKpi: false,
    employeePerf: false,
    teamActivity: false,
  });
  const toggleSection = useCallback((key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Check if user is an admin or has full-view access via email
  const userEmail = employeeData?.eMail?.toLowerCase() || '';
  const FULL_VIEW_EMAILS = [
    'arzu.dereli@habcarecro.com',
    'hulya.bayraktar@habcarecro.com',
    'nagihan.cicisarac@habcarecro.com',
    'unzile.yetkin@habcarecro.com'
  ];
  const isAdmin = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin') || FULL_VIEW_EMAILS.includes(userEmail);

  // SMO removal and worker removal permission: only super admin (turanemre1999@gmail.com) and arzu.dereli
  const canRemoveSMO = userEmail === 'turanemre1999@gmail.com' || userEmail === 'arzu.dereli@habcarecro.com';

  // Manual data entry permission: only admins and unzile.yetkin
  const MANUAL_ENTRY_EMAILS = ['unzile.yetkin@habcarecro.com'];
  const canManualEntry = userAuthorization.includes('admin') || userAuthorization.includes('Kısıtlı Admin') || MANUAL_ENTRY_EMAILS.includes(userEmail);

  // Manual data entry modal state
  const [showManualEntry, setShowManualEntry] = useState(false);

  // Incomplete tasks modal state
  const [showIncompleteTasksModal, setShowIncompleteTasksModal] = useState(false);

  // ── Date range filters for dashboard sections ─────────────────────────
  const getMonthStart = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  };
  const getToday = () => new Date().toISOString().split('T')[0];

  const [s1DateStart, setS1DateStart] = useState(getMonthStart);
  const [s1DateEnd, setS1DateEnd] = useState(getToday);
  const [s2DateStart, setS2DateStart] = useState(getMonthStart);
  const [s2DateEnd, setS2DateEnd] = useState(getToday);
  const [s3DateStart, setS3DateStart] = useState(getMonthStart);
  const [s3DateEnd, setS3DateEnd] = useState(getToday);
  const [s4DateStart, setS4DateStart] = useState(getMonthStart);
  const [s4DateEnd, setS4DateEnd] = useState(getToday);

  const filterVisitsByDate = useCallback((visits, start, end) => {
    if (!visits) return [];
    return visits.filter(v => {
      const d = v.date || v.firstVisitDate || '';
      if (!d) return false;
      if (start && d < start) return false;
      if (end && d > end) return false;
      return true;
    });
  }, []);

  const s1Visits = useMemo(() => filterVisitsByDate(allVisits, s1DateStart, s1DateEnd), [allVisits, s1DateStart, s1DateEnd, filterVisitsByDate]);
  const s2Visits = useMemo(() => filterVisitsByDate(allVisits, s2DateStart, s2DateEnd), [allVisits, s2DateStart, s2DateEnd, filterVisitsByDate]);
  const s3Visits = useMemo(() => filterVisitsByDate(allVisits, s3DateStart, s3DateEnd), [allVisits, s3DateStart, s3DateEnd, filterVisitsByDate]);

  const resetDateFilter = useCallback((section) => {
    const start = getMonthStart();
    const end = getToday();
    if (section === 1) { setS1DateStart(start); setS1DateEnd(end); }
    else if (section === 2) { setS2DateStart(start); setS2DateEnd(end); }
    else if (section === 3) { setS3DateStart(start); setS3DateEnd(end); }
    else if (section === 4) { setS4DateStart(start); setS4DateEnd(end); }
  }, []);

  const setAllTime = useCallback((section) => {
    if (section === 1) { setS1DateStart(''); setS1DateEnd(''); }
    else if (section === 2) { setS2DateStart(''); setS2DateEnd(''); }
    else if (section === 3) { setS3DateStart(''); setS3DateEnd(''); }
    else if (section === 4) { setS4DateStart(''); setS4DateEnd(''); }
  }, []);

  // ── Ekip sistem kullanımı (çevrimiçi süre / giriş istatistikleri) ──────
  // Nabız verisi Sayfa Yetkileri'ndeki "Çevrimiçi Olanlar" ile aynı kaynaktan
  // okunur; burada yalnızca seçili SMO'nun ekibine daraltılır. Geçmiş veriler
  // bölüm açılana kadar çekilmez (`enabled`), canlı durum ise başlıktaki
  // çevrimiçi sayısı için her zaman dinlenir.
  const teamActivity = useTeamActivity(workers, {
    enabled: expandedSections.teamActivity,
    startDate: s4DateStart,
    endDate: s4DateEnd,
  });

  // ── Incomplete Tasks computation ────────────────────────────────
  const incompleteTasks = useMemo(() => {
    if (!allVisits && !allCalendarJobs) return { visits: [], jobs: [], total: 0, byWorker: {} };

    // Get incomplete visits (status: false or undefined)
    const incompleteVisits = (allVisits || []).filter(visit => !visit.status).map(visit => {
      const worker = workers.find(w => w.id === visit.employeeId);
      return {
        ...visit,
        type: 'visit',
        workerName: worker ? `${worker.name} ${worker.surname}` : (visit.employeeName || 'Bilinmeyen Çalışan'),
        title: `${visit.patientId || 'Hasta'} - ${visit.visitType === 'home' ? 'Ev Viziti' : 'Hastane Viziti'}`,
        date: visit.firstVisitDate || visit.date
      };
    });

    // Get incomplete calendar jobs (status: false or undefined)
    const incompleteJobs = (allCalendarJobs || []).filter(job => !job.status).map(job => {
      const worker = workers.find(w => w.id === job.employeeId);
      return {
        ...job,
        type: 'job',
        workerName: worker ? `${worker.name} ${worker.surname}` : 'Bilinmeyen Çalışan',
        date: job.startDate
      };
    });

    // Combine and sort by date
    const allIncompleteTasks = [...incompleteVisits, ...incompleteJobs].sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });

    // Group by worker ID
    const byWorker = {};
    allIncompleteTasks.forEach(task => {
      const workerId = task.employeeId;
      if (!byWorker[workerId]) {
        byWorker[workerId] = [];
      }
      byWorker[workerId].push(task);
    });

    return {
      visits: incompleteVisits,
      jobs: incompleteJobs,
      all: allIncompleteTasks,
      total: allIncompleteTasks.length,
      byWorker: byWorker
    };
  }, [allVisits, allCalendarJobs, workers]);

  // ── Ev Viziti Sorun Takibi computation ────────────────────────────────
  const capaDashboard = useMemo(() => {
    if (!s1Visits || s1Visits.length === 0) return null;

    const homeVisits = s1Visits.filter(v => v.visitType === 'home');
    if (homeVisits.length === 0) return null;

    const coordinatorMap = {};

    homeVisits.forEach(v => {
      const empId = v.employeeId;
      const w = workers.find(wk => wk.id === empId);
      const empName = w ? `${w.name} ${w.surname}` : (v.employeeName || empId);

      if (!coordinatorMap[empId]) {
        coordinatorMap[empId] = {
          id: empId,
          name: empName,
          totalVisits: 0,
          totalIssues: 0,
          missedVisits: 0,
          aeSaeCount: 0,
          dcfCreatedCount: 0,
          visits: [],
          monthlyIssues: {},
        };
      }

      const c = coordinatorMap[empId];
      c.totalVisits++;
      c.visits.push(v);

      const ev = v.visitEvaluation || {};
      const vDate = v.date || v.firstVisitDate || '';
      const month = vDate ? vDate.substring(0, 7) : null;

      if (ev.issueOccurred === 'yes') {
        c.totalIssues++;
        if (month) {
          if (!c.monthlyIssues[month]) c.monthlyIssues[month] = { issues: 0, missed: 0 };
          c.monthlyIssues[month].issues++;
        }
      }
      if (ev.issueResultedInMiss === 'yes') {
        c.missedVisits++;
        if (month && c.monthlyIssues[month]) c.monthlyIssues[month].missed++;
      }
      if (ev.wasAeSaeTaken === 'yes') c.aeSaeCount++;
      if (ev.dcfCreated === 'yes') c.dcfCreatedCount++;
    });

    const coordinators = Object.values(coordinatorMap).sort((a, b) => a.name.localeCompare(b.name));

    const totals = {
      totalVisits: homeVisits.length,
      totalIssues: coordinators.reduce((s, c) => s + c.totalIssues, 0),
      missedVisits: coordinators.reduce((s, c) => s + c.missedVisits, 0),
      aeSaeCount: coordinators.reduce((s, c) => s + c.aeSaeCount, 0),
      dcfCreatedCount: coordinators.reduce((s, c) => s + c.dcfCreatedCount, 0),
    };

    // Bar chart: issues vs missed per coordinator
    const barChartData = coordinators.filter(c => c.totalIssues > 0 || c.missedVisits > 0).map(c => ({
      name: c.name.length > 15 ? c.name.substring(0, 14) + '…' : c.name,
      fullName: c.name,
      Sorun: c.totalIssues,
      Kaçırılan: c.missedVisits,
    }));

    // Pie chart: issue breakdown
    const pieChartData = [
      { name: 'Sorunlu Vizit', value: totals.totalIssues },
      { name: 'Sorunsuz Vizit', value: Math.max(0, totals.totalVisits - totals.totalIssues) },
    ].filter(d => d.value > 0);

    // Trend data (monthly)
    const allMonths = {};
    coordinators.forEach(c => {
      Object.entries(c.monthlyIssues).forEach(([month, data]) => {
        if (!allMonths[month]) allMonths[month] = { month, Sorun: 0, Kaçırılan: 0 };
        allMonths[month]['Sorun'] += data.issues;
        allMonths[month]['Kaçırılan'] += data.missed;
      });
    });
    const trendData = Object.values(allMonths).sort((a, b) => a.month.localeCompare(b.month));

    // AE/SAE & DCF overview
    const extrasData = [
      { name: 'AE/SAE Alınan', value: totals.aeSaeCount },
      { name: 'DCF Oluşturulan', value: totals.dcfCreatedCount },
    ].filter(d => d.value > 0);

    return { coordinators, totals, barChartData, pieChartData, trendData, extrasData };
  }, [s1Visits, workers]);

  // ── Issue drill-down helpers ───────────────────────────────────────────
  // Filter a pool of visits down to the ones matching a given "issue" metric.
  const filterVisitsByMetric = useCallback((pool, metric) => {
    return (pool || []).filter(v => {
      const ev = v.visitEvaluation || {};
      switch (metric) {
        case 'issues': return ev.issueOccurred === 'yes';
        case 'missed': return ev.issueResultedInMiss === 'yes';
        case 'deviation': return ev.protocolDeviation === 'yes';
        case 'capa': return ev.capaOpened === 'yes';
        case 'screenFail': return ev.screenFail === 'yes';
        case 'dropPatient': return ev.dropPatient === 'yes';
        case 'aesae': return ev.wasAeSaeTaken === 'yes';
        case 'dcf': return ev.dcfCreated === 'yes';
        case 'missingProcessEnd': return !ev.processEndDate;
        case 'overdue': {
          const start = v.date || v.firstVisitDate;
          if (!ev.slaTarget || !ev.processEndDate || !start) return false;
          const cycle = Math.ceil((new Date(ev.processEndDate) - new Date(start)) / 86400000);
          return cycle > parseFloat(ev.slaTarget);
        }
        default: return false;
      }
    });
  }, []);

  // Open the Issue Detail modal with a pre-filtered visit list.
  const openIssueModal = useCallback((title, pool, metric) => {
    const visits = metric ? filterVisitsByMetric(pool, metric) : (pool || []);
    if (visits.length === 0) return; // nothing to inspect
    setIssueModal({ title, visits });
  }, [filterVisitsByMetric]);

  // Authoritative projectId -> short title map, fetched once from the Projects
  // collection. Visits only store the Firestore projectId, so we join here.
  const [projectTitleMap, setProjectTitleMap] = useState({});
  useEffect(() => {
    const fetchProjectTitles = async () => {
      try {
        const snap = await getDocs(collection(db, 'Projects'));
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          map[d.id] = data.titleShort || data.titleLong || '';
        });
        setProjectTitleMap(map);
      } catch (e) {
        console.error('Error fetching project titles:', e);
      }
    };
    fetchProjectTitles();
  }, []);

  // Combined projectId -> short title map. The Projects collection is primary;
  // the workers' assignments fill any gap (e.g. a project deleted from Projects).
  const projectNameMap = useMemo(() => {
    const map = { ...projectTitleMap };
    workers.forEach(w => (w.projects || []).forEach(p => {
      if (p.projectId && p.projectTitle && !map[p.projectId]) map[p.projectId] = p.projectTitle;
    }));
    return map;
  }, [projectTitleMap, workers]);

  // ── Hastane (Site) Visit KPI computation ────────────────────────────
  const hastaneKpis = useMemo(() => {
    if (!allVisits || allVisits.length === 0) return null;

    const siteVisits = allVisits.filter(v => v.visitType === 'hastane' || (v.visitType && v.visitType !== 'home'));
    if (siteVisits.length === 0) return null;

    const total = siteVisits.length;
    const homeVisits = allVisits.filter(v => v.visitType === 'home');

    const r = n => Math.round(n * 10) / 10;
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    // Cycle times
    const cycleCalc = (arr) => {
      const vals = arr.flatMap(v => {
        const vDate = v.date || v.firstVisitDate;
        if (!vDate || !v.visitEvaluation?.processEndDate) return [];
        const days = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(vDate)) / 86400000);
        return days >= 0 ? [days] : [];
      });
      return vals.length > 0 ? r(vals.reduce((a, b) => a + b, 0) / vals.length) : '-';
    };
    const avgCycleTimeHome = cycleCalc(homeVisits);
    const avgCycleTimeSite = cycleCalc(siteVisits);
    const visitsMissingProcessEndDate = allVisits.filter(v => !v.visitEvaluation?.processEndDate).length;

    // Issues
    const issuesOccurred = siteVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const issueRate = pct(issuesOccurred, total);

    // Protocol deviations
    const protocolDeviations = siteVisits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const protocolDeviationRate = pct(protocolDeviations, total);

    // CAPA opened (from site visits)
    const capaOpenedCount = siteVisits.filter(v => v.visitEvaluation?.capaOpened === 'yes').length;

    // SLA
    const withSLA = siteVisits.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
    const slaMet = withSLA.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
    const slaMetRate = pct(slaMet, withSLA.length);
    const overdueVisits = withSLA.length - slaMet;

    // Screen Fail
    const screenFailCount = siteVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
    const screenFailRate = pct(screenFailCount, total);

    // Drop Patient
    const dropPatientCount = siteVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;
    const dropPatientRate = pct(dropPatientCount, total);

    // Key Rates chart data
    const keyRatesChart = [
      { name: 'Tarama Başarısız', Oran: screenFailRate },
      { name: 'Hasta Çıkışı', Oran: dropPatientRate },
      { name: 'Protokol Sapması', Oran: protocolDeviationRate },
      { name: 'SLA Karşılandı', Oran: slaMetRate },
    ];

    return {
      total,
      avgCycleTimeHome, avgCycleTimeSite, visitsMissingProcessEndDate,
      issuesOccurred, issueRate,
      protocolDeviationRate, capaOpenedCount,
      slaMetRate, overdueVisits,
      screenFailCount, screenFailRate,
      dropPatientCount, dropPatientRate,
      keyRatesChart,
    };
  }, [allVisits]);

  // ── Employee Performance Stats ────────────────────────────────────────
  const employeeStats = useMemo(() => {
    if (!s3Visits || s3Visits.length === 0 || workers.length === 0) return null;

    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;
    const r1 = n => Math.round(n * 10) / 10;

    const stats = workers.map(w => {
      const wVisits = s3Visits.filter(v => v.employeeId === w.id);
      const total = wVisits.length;
      if (total === 0) return null;

      const homeV = wVisits.filter(v => v.visitType === 'home');
      const siteV = wVisits.filter(v => v.visitType !== 'home');

      // KM (only travelDistanceBefore — the field users can fill)
      const totalKm = wVisits.reduce((sum, v) => {
        const ev = v.visitEvaluation || {};
        return sum + (parseFloat(ev.travelDistanceBefore) || 0);
      }, 0);
      const avgKmPerVisit = r1(total > 0 ? totalKm / total : 0);

      // eCRF & Source completion
      const ecrfDone = wVisits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
      const ecrfPct = pct(ecrfDone, total);
      const sourceDone = wVisits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
      const sourcePct = pct(sourceDone, total);

      // Deviations
      const deviationCount = wVisits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
      const deviationPct = r1(total > 0 ? deviationCount / total * 100 : 0);

      // CAPA Opened (from site visits only — capaOpened is in the site visit form)
      const capaOpenedCount = wVisits.filter(v =>
        v.visitEvaluation?.capaOpened === 'yes'
      ).length;

      // Screen Fail / Drop Patient
      const screenFailCount = wVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
      const dropPatientCount = wVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;

      // SLA
      const withSLA = wVisits.filter(v =>
        v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
      );
      const slaMet = withSLA.filter(v => {
        const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
        return cycle <= parseFloat(v.visitEvaluation.slaTarget);
      }).length;
      const slaMetPct = pct(slaMet, withSLA.length);
      const overdueCount = withSLA.length - slaMet;

      // Issue categories for aggregate chart
      const issueCategories = {};
      wVisits.forEach(v => {
        if (v.visitEvaluation?.issueOccurred === 'yes' && v.visitEvaluation?.issueCategory) {
          const cat = v.visitEvaluation.issueCategory;
          issueCategories[cat] = (issueCategories[cat] || 0) + 1;
        }
      });

      return {
        id: w.id, name: `${w.name} ${w.surname}`,
        totalVisits: total, homeVisits: homeV.length, siteVisits: siteV.length,
        totalKm: r1(totalKm), avgKmPerVisit, ecrfPct, sourcePct,
        deviationCount, capaOpenedCount,
        screenFailCount, dropPatientCount, slaMetPct, overdueCount, deviationPct,
        issueCategories,
      };
    }).filter(Boolean);

    if (stats.length === 0) return null;

    // ── Aggregate chart data ──
    const totalHomeV = s3Visits.filter(v => v.visitType === 'home');
    const totalSiteV = s3Visits.filter(v => v.visitType !== 'home');

    // 1. Home vs Site SLA Met %
    const slaCalc = arr => {
      const valid = arr.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
      const met = valid.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
      return pct(met, valid.length);
    };
    const homeVsSiteSla = [
      { name: 'Ev', 'SLA Karşılanma %': slaCalc(totalHomeV) },
      { name: 'Hastane', 'SLA Karşılanma %': slaCalc(totalSiteV) },
    ];

    // 2. KM per Nurse (top 10)
    const kmPerNurse = [...stats].sort((a, b) => b.totalKm - a.totalKm).slice(0, 10).map(s => ({
      name: s.name.length > 18 ? s.name.substring(0, 17) + '…' : s.name,
      fullName: s.name, KM: s.totalKm,
    }));

    // 3. Top Issue Categories
    const allCats = {};
    stats.forEach(s => Object.entries(s.issueCategories).forEach(([c, n]) => { allCats[c] = (allCats[c] || 0) + n; }));
    const issueCategoryLabels = { iv_access: 'IV Erişim', patient_compliance: 'Hasta Uyumu', documentation: 'Dokümantasyon', logistics: 'Lojistik', protocol: 'Protokol', equipment: 'Ekipman', other: 'Diğer' };
    const topIssueCategories = Object.entries(allCats).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([c, n]) => ({ name: issueCategoryLabels[c] || c, Adet: n }));

    // 4. Issue Rate: Home vs Site
    const issueRateHomeVsSite = [
      { name: 'Ev', Oran: pct(totalHomeV.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, totalHomeV.length) },
      { name: 'Hastane', Oran: pct(totalSiteV.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, totalSiteV.length) },
    ];

    return { stats, homeVsSiteSla, kmPerNurse, topIssueCategories, issueRateHomeVsSite };
  }, [s3Visits, workers]);

  // ── Unified Operational Summary (story-driven) ────────────────────────
  const computeOpSummary = useCallback((visits) => {
    if (!visits || visits.length === 0) return null;

    const total = visits.length;
    const homeVisits = visits.filter(v => v.visitType === 'home');
    const siteVisits = visits.filter(v => v.visitType !== 'home');
    const pct = (n, d) => d > 0 ? Math.round(n / d * 100) : 0;

    // Quality rate
    const issueCount = visits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
    const qualityRate = pct(total - issueCount, total);

    // SLA compliance (all visits)
    const withSLA = visits.filter(v =>
      v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate
    );
    const slaMet = withSLA.filter(v => {
      const cycle = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000);
      return cycle <= parseFloat(v.visitEvaluation.slaTarget);
    }).length;
    const slaRate = pct(slaMet, withSLA.length);

    // Documentation completeness
    const ecrfDone = visits.filter(v => v.visitEvaluation?.ecrfCompletionDate).length;
    const sourceDone = visits.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length;
    const signDone = visits.filter(v => v.visitEvaluation?.signatureCompletionDate).length;
    const docRate = Math.round((pct(ecrfDone, total) + pct(sourceDone, total) + pct(signDone, total)) / 3);

    // Safety & quality metrics
    const aeSaeCount = visits.filter(v => v.visitEvaluation?.wasAeSaeTaken === 'yes').length;
    const dcfCount = visits.filter(v => v.visitEvaluation?.dcfCreated === 'yes').length;
    const missedCount = visits.filter(v => v.visitEvaluation?.issueResultedInMiss === 'yes').length;
    const deviationCount = visits.filter(v => v.visitEvaluation?.protocolDeviation === 'yes').length;
    const deviationRate = pct(deviationCount, total);
    const capaCount = visits.filter(v => v.visitEvaluation?.capaOpened === 'yes').length;
    const screenFail = siteVisits.filter(v => v.visitEvaluation?.screenFail === 'yes').length;
    const dropPatient = siteVisits.filter(v => v.visitEvaluation?.dropPatient === 'yes').length;
    const missingProcessEnd = visits.filter(v => !v.visitEvaluation?.processEndDate).length;

    // KM
    const totalKm = Math.round(visits.reduce((sum, v) =>
      sum + (parseFloat(v.visitEvaluation?.travelDistanceBefore) || 0), 0));

    // Avg cycle times
    const cycleCalc = (arr) => {
      const vals = arr.flatMap(v => {
        const vDate = v.date || v.firstVisitDate;
        if (!vDate || !v.visitEvaluation?.processEndDate) return [];
        const days = Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(vDate)) / 86400000);
        return days >= 0 ? [days] : [];
      });
      return vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length * 10) / 10 : null;
    };
    const avgCycleHome = cycleCalc(homeVisits);
    const avgCycleSite = cycleCalc(siteVisits);

    // Alerts
    const alerts = [];
    if (withSLA.length > 0 && slaRate < 80) alerts.push({ type: 'danger', metric: 'overdue', text: `SLA karşılanma oranı %${slaRate} — hedefin altında` });
    if (pct(issueCount, total) > 10) alerts.push({ type: 'danger', metric: 'issues', text: `Sorun oranı %${pct(issueCount, total)} — yüksek seviyede` });
    if (deviationRate > 5) alerts.push({ type: 'warning', metric: 'deviation', text: `Protokol sapma oranı %${deviationRate}` });
    if (missingProcessEnd > total * 0.2) alerts.push({ type: 'warning', metric: 'missingProcessEnd', text: `${missingProcessEnd} vizitte süreç bitiş tarihi eksik` });
    if (missedCount > 0) alerts.push({ type: 'danger', metric: 'missed', text: `${missedCount} vizit kaçırıldı` });
    if (aeSaeCount > 0) alerts.push({ type: 'info', metric: 'aesae', text: `${aeSaeCount} AE/SAE rapor edildi` });

    // Monthly trend
    const monthlyData = {};
    visits.forEach(v => {
      const date = v.date || v.firstVisitDate || '';
      const month = date ? date.substring(0, 7) : null;
      if (!month) return;
      if (!monthlyData[month]) monthlyData[month] = { month, Ev: 0, Hastane: 0, Sorun: 0 };
      if (v.visitType === 'home') monthlyData[month].Ev++;
      else monthlyData[month].Hastane++;
      if (v.visitEvaluation?.issueOccurred === 'yes') monthlyData[month].Sorun++;
    });
    const monthlyTrend = Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));

    // Ev vs Hastane comparison chart
    const compSlaCalc = (arr) => {
      const valid = arr.filter(v => v.visitEvaluation?.slaTarget && (v.date || v.firstVisitDate) && v.visitEvaluation?.processEndDate);
      const met = valid.filter(v => Math.ceil((new Date(v.visitEvaluation.processEndDate) - new Date(v.date || v.firstVisitDate)) / 86400000) <= parseFloat(v.visitEvaluation.slaTarget)).length;
      return pct(met, valid.length);
    };
    const compDocCalc = (arr) => Math.round((pct(arr.filter(v => v.visitEvaluation?.ecrfCompletionDate).length, arr.length) + pct(arr.filter(v => v.visitEvaluation?.sourceDocCompletionDate).length, arr.length)) / 2);

    const comparisonChart = [
      { name: 'Sorun Oranı (%)', Ev: pct(homeVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, homeVisits.length), Hastane: pct(siteVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length, siteVisits.length) },
      { name: 'SLA Uyumu (%)', Ev: compSlaCalc(homeVisits), Hastane: compSlaCalc(siteVisits) },
      { name: 'Dokümantasyon (%)', Ev: compDocCalc(homeVisits), Hastane: compDocCalc(siteVisits) },
    ];

    // Quality KPI tiles
    const qualityTiles = [
      { label: 'Protokol Sapması', metric: 'deviation', count: deviationCount, rate: deviationRate, color: deviationCount > 0 ? '#c53030' : '#276749' },
      { label: 'CAPA Açılan', metric: 'capa', count: capaCount, rate: null, color: capaCount > 0 ? '#c05621' : '#276749' },
      { label: 'Screen Fail', metric: 'screenFail', count: screenFail, rate: pct(screenFail, siteVisits.length), color: screenFail > 0 ? '#c05621' : '#276749' },
      { label: 'Drop Patient', metric: 'dropPatient', count: dropPatient, rate: pct(dropPatient, siteVisits.length), color: dropPatient > 0 ? '#c05621' : '#276749' },
      { label: 'AE/SAE', metric: 'aesae', count: aeSaeCount, rate: null, color: aeSaeCount > 0 ? '#9b2c2c' : '#276749' },
      { label: 'DCF', metric: 'dcf', count: dcfCount, rate: null, color: dcfCount > 0 ? '#744210' : '#276749' },
    ];

    return {
      total, homeCount: homeVisits.length, siteCount: siteVisits.length,
      qualityRate, slaRate, docRate, totalKm,
      issueCount, missedCount, deviationCount, capaCount,
      screenFail, dropPatient, aeSaeCount, dcfCount,
      missingProcessEnd, overdueCount: withSLA.length - slaMet,
      avgCycleHome, avgCycleSite,
      alerts, monthlyTrend, comparisonChart, qualityTiles,
    };
  }, []);

  const section1Summary = useMemo(() => computeOpSummary(s1Visits), [s1Visits, computeOpSummary]);
  const section2Summary = useMemo(() => computeOpSummary(s2Visits), [s2Visits, computeOpSummary]);

  // Fetch all SMOs from Projects collection (not Centers)
  // This ensures that even if a center is deleted from Centers collection,
  // it will still appear in SMO Lead Reports as long as it exists in Projects
  useEffect(() => {
    const fetchAllSMOs = async () => {
      try {
        setLoadingSMOs(true);
        
        // Fetch all projects to get center information
        const projectsRef = collection(db, 'Projects');
        const projectsSnapshot = await getDocs(projectsRef);
        
        console.log('📦 Fetching SMOs from Projects collection');
        console.log('📦 Total projects found:', projectsSnapshot.docs.length);
        
        // First pass: Build a map of all centers and their SMO IDs from Centers collection
        const centersRef = collection(db, 'Centers');
        const centersSnapshot = await getDocs(centersRef);
        const centerToSmoMap = {}; // centerId -> smoId (PRIMER SMO)
        const centerDetailsFromCenters = {}; // centerId -> { name, city, district, status }
        
        centersSnapshot.docs.forEach(centerDoc => {
          const centerData = centerDoc.data();
          const centerId = centerDoc.id;
          const smoId = centerData.SMO;
          
          // Her merkez için bilgileri kaydet (SMO olsun olmasın)
          centerDetailsFromCenters[centerId] = {
            name: centerData.Adı || '',
            city: centerData.İl || '',
            district: centerData.İlçe || '',
            status: centerData.Durum || 'Aktif'
          };
          
          if (smoId) {
            centerToSmoMap[centerId] = smoId;
          }
        });
        
        console.log('📍 Total centers in Centers collection:', centersSnapshot.docs.length);
        console.log('📍 Centers with SMO assignment:', Object.keys(centerToSmoMap).length);
        console.log('📍 Centers without city field:', centersSnapshot.docs.filter(doc => !doc.data().İl).length);
        
        console.log('📍 Centers from Centers collection:', Object.keys(centerToSmoMap).length);
        
        // Initialize smoMap with all primer SMOs from Centers collection
        const smoMap = {}; // smoId -> { id, centers: [] }
        const processedCentersBySmo = {}; // smoId -> Set(centerId) to track which centers we've added to which SMO
        
        // Add all primer SMO assignments from Centers collection
        Object.entries(centerToSmoMap).forEach(([centerId, smoId]) => {
          if (!smoMap[smoId]) {
            smoMap[smoId] = { id: smoId, centers: [] };
            processedCentersBySmo[smoId] = new Set();
          }
          
          if (!processedCentersBySmo[smoId].has(centerId)) {
            const centerDetails = centerDetailsFromCenters[centerId];
            
            console.log(`✅ Primer SMO: ${smoId} <- Center: ${centerId} | Name: "${centerDetails?.name}" | City: "${centerDetails?.city}" | District: "${centerDetails?.district}"`);
            
            smoMap[smoId].centers.push({
              id: centerId,
              name: centerDetails.name,
              city: centerDetails.city,
              district: centerDetails.district,
              status: centerDetails.status,
              isDeleted: false,
              isPrimerSmo: true // From Centers collection = Primer
            });
            processedCentersBySmo[smoId].add(centerId);
          }
        });
        
        console.log('📍 Primer SMOs from Centers:', Object.keys(smoMap).length);

        // Add backup SMO assignments from Centers collection (SMOBackup field)
        centersSnapshot.docs.forEach(centerDoc => {
          const centerData = centerDoc.data();
          const centerId = centerDoc.id;
          const backupSmoId = centerData.SMOBackup;
          
          if (!backupSmoId) return; // No backup SMO for this center
          
          const primerSmoId = centerToSmoMap[centerId];
          
          // Skip if backup SMO is same as primer SMO
          if (backupSmoId === primerSmoId) {
            console.log(`⚠️ Skipping: Backup SMO same as primer for center ${centerId}`);
            return;
          }
          
          // Initialize backup SMO in map if not exists
          if (!smoMap[backupSmoId]) {
            smoMap[backupSmoId] = { id: backupSmoId, centers: [] };
            processedCentersBySmo[backupSmoId] = new Set();
          }
          
          // Add center to backup SMO if not already added
          if (!processedCentersBySmo[backupSmoId].has(centerId)) {
            const centerDetails = centerDetailsFromCenters[centerId];
            
            // If not in centerDetailsFromCenters map, get directly from centerData
            if (!centerDetails) {
              console.warn(`⚠️ Center ${centerId} not in centerDetailsFromCenters, reading directly from Centers document`);
            }
            
            const finalCenterDetails = centerDetails || {
              name: centerData.Adı || '',
              city: centerData.İl || '',
              district: centerData.İlçe || '',
              status: centerData.Durum || 'Aktif'
            };
            
            console.log(`✅ Backup SMO: ${backupSmoId} <- Center: ${centerId} | Name: "${finalCenterDetails.name}" | City: "${finalCenterDetails.city}" | District: "${finalCenterDetails.district}"`);
            
            smoMap[backupSmoId].centers.push({
              id: centerId,
              name: finalCenterDetails.name,
              city: finalCenterDetails.city,
              district: finalCenterDetails.district,
              status: finalCenterDetails.status,
              isDeleted: false,
              isPrimerSmo: false // Backup SMO
            });
            processedCentersBySmo[backupSmoId].add(centerId);
          }
        });
        
        console.log('📍 Backup SMOs added from Centers');

        // Process projects to handle deleted centers
        for (const projectDoc of projectsSnapshot.docs) {
          const projectData = projectDoc.data();
          const employees = projectData.employees || [];
          
          // Group employees by center in this project
          const centerEmployeesMap = {}; // centerId -> [employees]
          
          employees.forEach(emp => {
            const centerId = emp.centerId;
            if (!centerId) return;
            
            if (!centerEmployeesMap[centerId]) {
              centerEmployeesMap[centerId] = [];
            }
            centerEmployeesMap[centerId].push(emp);
          });
          
          // Process each center - only for deleted center recovery
          for (const [centerId, centerEmployees] of Object.entries(centerEmployeesMap)) {
            // Skip if center exists in Centers collection
            if (centerToSmoMap[centerId]) continue;

            const centerName = centerEmployees[0]?.centerName || 'Bilinmeyen Merkez';

            // Try to get center details from Centers collection even if no SMO assigned
            let centerDetails = {
              name: centerName,
              city: '',
              district: '',
              status: 'Aktif'
            };

            // Check if center exists in Centers collection (but maybe without SMO)
            const centerDoc = centersSnapshot.docs.find(doc => doc.id === centerId);
            if (centerDoc) {
              const centerData = centerDoc.data();
              centerDetails = {
                name: centerData.Adı || centerName,
                city: centerData.İl || '',
                district: centerData.İlçe || '',
                status: centerData.Durum || 'Aktif'
              };
            } else {
              // Center not in Centers collection - try to extract city from centerName
              // Common patterns: "İzmir Şehir Hastanesi", "Ege Üniversitesi Tıp Fakültesi Hastanesi"
              const cityPatterns = {
                'İstanbul': ['İstanbul', 'Cerrahpaşa', 'Şişli', 'Kartal', 'Haydarpaşa', 'Bağcılar', 'Bakırköy', 'Beşiktaş', 'Fatih', 'Üsküdar', 'Kadıköy', 'Pendik', 'Ümraniye'],
                'Ankara': ['Ankara', 'Hacettepe', 'Gazi Üniversitesi'],
                'İzmir': ['İzmir', 'Ege Üniversitesi', 'Dokuz Eylül', '9 Eylül', 'Katip Çelebi', 'Tepecik', 'Bornova', 'Karşıyaka', 'Buca', 'Çiğli', 'Bayraklı', 'Balçova'],
                'Bursa': ['Bursa', 'Uludağ Üniversitesi'],
                'Antalya': ['Antalya', 'Akdeniz Üniversitesi'],
                'Adana': ['Adana', 'Çukurova Üniversitesi'],
                'Konya': ['Konya', 'Selçuk Üniversitesi', 'Necmettin Erbakan'],
                'Mersin': ['Mersin'],
                'Gaziantep': ['Gaziantep'],
                'Kocaeli': ['Kocaeli', 'İzmit'],
                'Diyarbakır': ['Diyarbakır', 'Dicle Üniversitesi'],
                'Kayseri': ['Kayseri', 'Erciyes Üniversitesi'],
                'Eskişehir': ['Eskişehir', 'Osmangazi Üniversitesi'],
                'Samsun': ['Samsun', 'Ondokuz Mayıs Üniversitesi'],
                'Trabzon': ['Trabzon', 'Karadeniz Teknik Üniversitesi']
              };
              
              let extractedCity = '';
              for (const [city, patterns] of Object.entries(cityPatterns)) {
                for (const pattern of patterns) {
                  if (centerName.includes(pattern)) {
                    extractedCity = city;
                    break;
                  }
                }
                if (extractedCity) break;
              }
              
              centerDetails = {
                name: centerName,
                city: extractedCity,
                district: '',
                status: 'Aktif'
              };
              
              if (extractedCity) {
                console.log(`📍 Extracted city "${extractedCity}" from center name: ${centerName}`);
              } else {
                console.warn(`⚠️ Could not extract city from center name: ${centerName}`);
              }
            }
            
            const centerInfo = {
              id: centerId,
              name: centerDetails.name,
              city: centerDetails.city,
              district: centerDetails.district,
              status: centerDetails.status,
              isDeleted: !centerDoc // Mark as deleted only if not in Centers collection at all
            };

            // If center is deleted, try to recover the SMO from employee records
            const empWithSmo = centerEmployees.find(emp => emp.smoId);
            if (empWithSmo) {
              const recoveredSmoId = empWithSmo.smoId;
              
              if (!smoMap[recoveredSmoId]) {
                smoMap[recoveredSmoId] = {
                  id: recoveredSmoId,
                  centers: []
                };
                processedCentersBySmo[recoveredSmoId] = new Set();
              }
              
              if (!processedCentersBySmo[recoveredSmoId].has(centerId)) {
                console.log(`🔄 Recovering deleted center: ${centerId} for SMO ${recoveredSmoId}`);
                smoMap[recoveredSmoId].centers.push({
                  ...centerInfo,
                  isPrimerSmo: true // Assume primer for recovered SMOs
                });
                processedCentersBySmo[recoveredSmoId].add(centerId);
              }
            }
          }
        }
        
        console.log('📊 SMO Map created:', Object.keys(smoMap).length, 'SMOs found');
        
        // Debug: Log SMO centers with primer/backup info
        Object.entries(smoMap).forEach(([smoId, smoData]) => {
          const primerCenters = smoData.centers.filter(c => c.isPrimerSmo);
          const backupCenters = smoData.centers.filter(c => !c.isPrimerSmo);
          if (backupCenters.length > 0) {
            console.log(`  SMO ${smoId}: ${primerCenters.length} primer, ${backupCenters.length} backup centers`);
          }
        });
        
        // Fetch employee details for each SMO in parallel
        const smoIds = Object.keys(smoMap);
        const empDocs = await Promise.all(
          smoIds.map(smoId => getDoc(doc(db, 'Employees', smoId)).catch(() => null))
        );
        const smoListData = smoIds.map((smoId, idx) => {
          const empDoc = empDocs[idx];
          if (!empDoc || !empDoc.exists()) return null;
          const empData = empDoc.data();
          return {
            id: smoId,
            name: empData.eName || '',
            surname: empData.eSurname || '',
            email: empData.eMail || '',
            phone: empData.phone || '',
            centers: smoMap[smoId].centers,
            centerCount: smoMap[smoId].centers.length
          };
        }).filter(Boolean);
        
        // Sort by name
        smoListData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));

        setSmoList(smoListData);
        setLoadingSMOs(false);
      } catch (error) {
        console.error('Error fetching SMOs:', error);
        toast.error('SMO listesi yüklenirken hata oluştu!');
        setLoadingSMOs(false);
      }
    };

    fetchAllSMOs();
  }, []);

  // Find projects where the current user is assigned as a "backup" SMO Lead.
  // For each such project, build a delegated view that mirrors the primer
  // SMO Lead's data, scoped to that single project.
  useEffect(() => {
    if (isAdmin || !employeeId) {
      setDelegatedSmoViews([]);
      return;
    }
    const fetchBackupDelegations = async () => {
      try {
        const [projectsSnap, centersSnap] = await Promise.all([
          getDocs(collection(db, 'Projects')),
          getDocs(collection(db, 'Centers')),
        ]);

        // primerSmoId -> [center objects]
        const primerCentersMap = {};
        centersSnap.docs.forEach((cd) => {
          const c = cd.data();
          if (!c.SMO) return;
          if (!primerCentersMap[c.SMO]) primerCentersMap[c.SMO] = [];
          primerCentersMap[c.SMO].push({
            id: cd.id,
            name: c['Adı'] || '',
            city: c['İl'] || '',
            district: c['İlçe'] || '',
            status: c['Durum'] || 'Aktif',
          });
        });

        // Find delegations: (primerSmoId, projectId)
        const delegations = [];
        const primerIdsToFetch = new Set();
        const projectsById = {};

        projectsSnap.docs.forEach((pd) => {
          const data = pd.data();
          projectsById[pd.id] = data;
          const employees = data.employees || [];
          const isBackupSmoLeadHere = employees.some(
            (e) =>
              e.id === employeeId &&
              String(e.role || '').toLowerCase() === 'smo lead' &&
              String(e.workerType || '').toLowerCase() === 'backup'
          );
          if (!isBackupSmoLeadHere) return;

          const primers = employees.filter(
            (e) =>
              String(e.role || '').toLowerCase() === 'smo lead' &&
              String(e.workerType || '').toLowerCase() === 'primer' &&
              e.id && e.id !== employeeId
          );
          if (primers.length === 0) return;

          // Dedupe primers within the project
          const seen = new Set();
          primers.forEach((p) => {
            if (seen.has(p.id)) return;
            seen.add(p.id);
            delegations.push({
              primerId: p.id,
              projectId: pd.id,
              projectTitle: data.titleShort || data.titleLong || '',
            });
            primerIdsToFetch.add(p.id);
          });
        });

        if (delegations.length === 0) {
          setDelegatedSmoViews([]);
          return;
        }

        const primerIds = Array.from(primerIdsToFetch);
        const primerDocs = await Promise.all(
          primerIds.map((id) => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );
        const primerDataMap = {};
        primerIds.forEach((id, idx) => {
          const d = primerDocs[idx];
          if (d && d.exists()) primerDataMap[id] = d.data();
        });

        const views = delegations
          .map((d) => {
            const primerData = primerDataMap[d.primerId];
            if (!primerData) return null;
            const allPrimerCenters = primerCentersMap[d.primerId] || [];
            const projectData = projectsById[d.projectId] || {};
            const projEmployees = projectData.employees || [];
            const centerIdsInProject = new Set(
              projEmployees.map((e) => e.centerId).filter(Boolean)
            );
            // Scope primer's centers to those actually used in this project
            const scoped = allPrimerCenters.filter((c) =>
              centerIdsInProject.has(c.id)
            );
            const centers = scoped.length > 0 ? scoped : allPrimerCenters;
            return {
              id: d.primerId,
              viewKey: `${d.primerId}__${d.projectId}`,
              name: primerData.eName || '',
              surname: primerData.eSurname || '',
              email: primerData.eMail || '',
              phone: primerData.phone || '',
              centers,
              centerCount: centers.length,
              restrictToProjectId: d.projectId,
              restrictToProjectTitle: d.projectTitle,
              delegatedFromBackup: true,
            };
          })
          .filter(Boolean);

        views.sort((a, b) => {
          const an = `${a.name} ${a.surname}`;
          const bn = `${b.name} ${b.surname}`;
          const c = an.localeCompare(bn, 'tr');
          if (c !== 0) return c;
          return (a.restrictToProjectTitle || '').localeCompare(
            b.restrictToProjectTitle || '',
            'tr'
          );
        });

        setDelegatedSmoViews(views);
      } catch (err) {
        console.error('Error loading backup SMO delegations:', err);
      }
    };
    fetchBackupDelegations();
  }, [isAdmin, employeeId]);

  // Find centers where the current user is set as the "Yedek SMO" (backup).
  // Group them by the center's primer SMO and build a delegated view per SMO,
  // mirroring that SMO Lead's reports but scoped to the backup-assigned centers.
  useEffect(() => {
    if (isAdmin || !employeeId) {
      setCenterBackupViews([]);
      return;
    }
    const fetchCenterBackupDelegations = async () => {
      try {
        const centersSnap = await getDocs(collection(db, 'Centers'));

        // primerSmoId -> [center objects] for centers where this user is backup
        const backupCentersByPrimer = {};
        centersSnap.docs.forEach((cd) => {
          const c = cd.data();
          if (c.SMOBackup !== employeeId) return;
          const primerSmoId = c.SMO;
          if (!primerSmoId || primerSmoId === employeeId) return; // need a distinct primer
          if (!backupCentersByPrimer[primerSmoId]) backupCentersByPrimer[primerSmoId] = [];
          backupCentersByPrimer[primerSmoId].push({
            id: cd.id,
            name: c['Adı'] || '',
            city: c['İl'] || '',
            district: c['İlçe'] || '',
            status: c['Durum'] || 'Aktif',
          });
        });

        const primerIds = Object.keys(backupCentersByPrimer);
        if (primerIds.length === 0) {
          setCenterBackupViews([]);
          return;
        }

        const primerDocs = await Promise.all(
          primerIds.map((id) => getDoc(doc(db, 'Employees', id)).catch(() => null))
        );

        const views = primerIds
          .map((primerId, idx) => {
            const d = primerDocs[idx];
            if (!d || !d.exists()) return null;
            const primerData = d.data();
            const centers = backupCentersByPrimer[primerId];
            return {
              id: primerId,
              viewKey: `centerbackup__${primerId}`,
              name: primerData.eName || '',
              surname: primerData.eSurname || '',
              email: primerData.eMail || '',
              phone: primerData.phone || '',
              centers,
              centerCount: centers.length,
              delegatedFromCenterBackup: true,
            };
          })
          .filter(Boolean);

        views.sort((a, b) =>
          `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`, 'tr')
        );

        setCenterBackupViews(views);
      } catch (err) {
        console.error('Error loading center backup delegations:', err);
      }
    };
    fetchCenterBackupDelegations();
  }, [isAdmin, employeeId]);

  // Auto-select own SMO for non-admin SMO Lead users, or a delegated view
  // when the user is only a backup SMO Lead.
  useEffect(() => {
    if (isAdmin || selectedSMO || !employeeId) return;
    const ownSmo = smoList.find((s) => s.id === employeeId);
    if (ownSmo) {
      setSelectedSMO({ ...ownSmo, viewKey: ownSmo.id });
      return;
    }
    const allDelegated = [...delegatedSmoViews, ...centerBackupViews];
    if (allDelegated.length === 1) {
      setSelectedSMO(allDelegated[0]);
    }
  }, [smoList, delegatedSmoViews, centerBackupViews, isAdmin, employeeId, selectedSMO]);

  // Fetch workers when an SMO is selected
  const fetchWorkersForSMO = useCallback(async () => {
    if (!selectedSMO) {
      setWorkers([]);
      setSmoCenters([]);
      return;
    }

    setLoadingWorkers(true);
    setExpandedWorkers({});
    
    try {
      // Get all center IDs for this SMO
      const centerIds = selectedSMO.centers.map(c => c.id);
      setSmoCenters(selectedSMO.centers);
      
      console.log('🏥 SMO Centers Data:', selectedSMO.centers.map(c => ({
        id: c.id,
        name: c.name,
        city: c.city,
        district: c.district,
        isPrimer: c.isPrimerSmo
      })));
      
      console.log('🔄 Fetching workers for SMO:', selectedSMO.name, selectedSMO.surname);
      console.log('📍 Center IDs:', centerIds);
      
      // Fetch all projects and find employees assigned to these centers
      const projectsRef = collection(db, 'Projects');
      const projectsSnapshot = await getDocs(projectsRef);
      
      console.log('📦 Total projects found:', projectsSnapshot.docs.length);
      
      // First pass: collect all center employees and unique employee IDs
      const empProjectMap = {}; // empId -> { emp, projects: [] }
      
      for (const projectDoc of projectsSnapshot.docs) {
        // For backup-SMO-Lead delegated views: only include the one project
        // this user was granted access to.
        if (selectedSMO.restrictToProjectId && projectDoc.id !== selectedSMO.restrictToProjectId) {
          continue;
        }
        const projectData = projectDoc.data();
        const projectEmployees = projectData.employees || [];

        const centerEmployees = projectEmployees.filter(emp =>
          centerIds.includes(emp.centerId)
        );
        
        if (centerEmployees.length > 0) {
          console.log(`📋 Project: ${projectData.titleShort || projectData.titleLong}`, {
            totalEmployees: projectEmployees.length,
            centerEmployees: centerEmployees.length
          });
        }
        
        for (const emp of centerEmployees) {
          // workerType: 'primer' veya 'backup' (string)
          // blindStatus: 'blinded' veya 'unblinded' (string)
          // Güvenli dönüşüm - hem string hem de undefined/null durumlarını kontrol et
          const isPrimer = emp.workerType === 'primer' || emp.workerType === 'Primer';
          const isBlinded = emp.blindStatus === 'blinded' || emp.blindStatus === 'Blinded';
          
          const projectInfo = {
            projectId: projectDoc.id,
            // String(): sayısal kaydedilmiş proje kodları arama/sıralamada
            // metin işlemlerine girdiği için tipi burada sabitliyoruz.
            projectTitle: String(projectData.titleShort || projectData.titleLong || ''),
            role: emp.role || '',
            rate: emp.rate,
            centerId: emp.centerId,
            centerName: emp.centerName || selectedSMO.centers.find(c => c.id === emp.centerId)?.name || '',
            assignedAt: emp.assignedAt,
            status: emp.status || 'active',
            // Merkezden ayrılmış atamalar rapordan düşmez, rozetle işaretlenir.
            isArchived: isArchivedAssignment(emp),
            unassignedAt: emp.unassignedAt || '',
            isPrimer: isPrimer, // Primer/Backup bilgisi
            isBlinded: isBlinded, // Blinded/Unblinded bilgisi
            isFinished: projectData.isFinished === true // Proje bitti mi
          };

          console.log(`👤 Employee: ${emp.name} ${emp.surname} | Project: ${projectInfo.projectTitle}`, {
            rawWorkerType: emp.workerType,
            rawBlindStatus: emp.blindStatus,
            convertedIsPrimer: projectInfo.isPrimer,
            convertedIsBlinded: projectInfo.isBlinded,
            centerId: emp.centerId,
            centerName: projectInfo.centerName
          });

          if (empProjectMap[emp.id]) {
            empProjectMap[emp.id].projects.push(projectInfo);
          } else {
            empProjectMap[emp.id] = { emp, projects: [projectInfo] };
          }
        }
      }

      // Fetch all unique employee details in parallel
      const uniqueEmpIds = Object.keys(empProjectMap);
      console.log('👥 Unique employees found:', uniqueEmpIds.length);
      
      const empDocs = await Promise.all(
        uniqueEmpIds.map(id => getDoc(doc(db, 'Employees', id)).catch(() => null))
      );
      const empDataMap = {};
      uniqueEmpIds.forEach((id, idx) => {
        const empDoc = empDocs[idx];
        empDataMap[id] = empDoc && empDoc.exists() ? empDoc.data() : null;
      });

      // Build workersData
      let workersData = uniqueEmpIds.map(id => {
        const { emp, projects } = empProjectMap[id];
        const employeeData = empDataMap[id];
        return {
          id,
          name: emp.name || '',
          surname: emp.surname || '',
          email: employeeData?.eMail || '',
          phone: employeeData?.phone || '',
          address: employeeData?.address || '',
          projects
        };
      });

      // Sort workers by name
      workersData.sort((a, b) => `${a.name} ${a.surname}`.localeCompare(`${b.name} ${b.surname}`));
      
      console.log('✅ Workers data ready:', workersData.length);
      
      setWorkers(workersData);
      setLoadingWorkers(false);
      toast.success('Çalışan verileri güncellendi!');
    } catch (error) {
      console.error('❌ Error fetching workers:', error);
      toast.error('Çalışan bilgileri yüklenirken hata oluştu!');
      setLoadingWorkers(false);
    }
  }, [selectedSMO]);

  useEffect(() => {
    fetchWorkersForSMO();
  }, [fetchWorkersForSMO]);

  // Fetch visits for workers under selected SMO (batched queries)
  useEffect(() => {
    if (workers.length === 0) { setAllVisits([]); return; }
    const fetchVisits = async () => {
      setLoadingVisits(true);
      try {
        const workerIds = workers.map(w => w.id);
        // Firestore 'in' supports max 30 values — batch into chunks
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        let visits = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        // Restrict to the delegated project for backup-SMO-Lead views
        if (selectedSMO?.restrictToProjectId) {
          visits = visits.filter(v => v.projectId === selectedSMO.restrictToProjectId);
        }
        setAllVisits(visits);
      } catch (e) {
        console.error('Error fetching visits for dashboard:', e);
      } finally {
        setLoadingVisits(false);
      }
    };
    fetchVisits();
  }, [workers, selectedSMO]);

  // Fetch calendar jobs for workers under selected SMO (batched queries)
  useEffect(() => {
    if (workers.length === 0) { setAllCalendarJobs([]); return; }
    const fetchJobs = async () => {
      setLoadingJobs(true);
      try {
        const workerIds = workers.map(w => w.id);
        // Firestore 'in' supports max 30 values — batch into chunks
        const BATCH_SIZE = 30;
        const batches = [];
        for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
          batches.push(workerIds.slice(i, i + BATCH_SIZE));
        }
        const results = await Promise.all(
          batches.map(batch => {
            const q = query(collection(db, 'CalendarJobs'), where('employeeId', 'in', batch));
            return getDocs(q);
          })
        );
        let jobs = results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() })));
        if (selectedSMO?.restrictToProjectId) {
          jobs = jobs.filter(j => j.projectId === selectedSMO.restrictToProjectId);
        }
        setAllCalendarJobs(jobs);
      } catch (e) {
        console.error('Error fetching calendar jobs for dashboard:', e);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchJobs();
  }, [workers, selectedSMO]);

  // Apply dark mode class
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Scroll handlers
  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [workers]);

  useEffect(() => {
    const checkScroll = () => {
      if (tableContainerRef.current) {
        const { scrollWidth, clientWidth } = tableContainerRef.current;
        setShowRightScroll(scrollWidth > clientWidth);
      }
    };
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [workers]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + (direction === 'right' ? scrollAmount : -scrollAmount);
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Handle SMO card click
  const handleSMOClick = (smo) => {
    const smoKey = smo.viewKey || smo.id;
    const currentKey = selectedSMO?.viewKey || selectedSMO?.id;
    if (currentKey === smoKey) {
      setSelectedSMO(null); // Deselect if already selected
      setCollapsedCities({}); // Reset collapsed cities when deselecting
    } else {
      setSelectedSMO({ ...smo, viewKey: smoKey });
      setCollapsedCities({}); // Reset collapsed cities for new SMO selection
    }
  };

  // Toggle expanded worker row
  const toggleWorkerExpand = (workerId) => {
    setExpandedWorkers(prev => ({
      ...prev,
      [workerId]: !prev[workerId]
    }));
  };

  // Open visits modal for a specific employee + project
  const handleViewVisits = useCallback((worker, project) => {
    // Use already-loaded allVisits instead of making a fresh Firestore query
    const workerVisits = allVisits.filter(
      v => v.employeeId === worker.id && v.projectId === project.projectId
    );

    if (workerVisits.length === 0) {
      toast.info('Bu çalışanın bu projedeki visit\'i bulunamadı.');
      return;
    }

    setSelectedEmployee({
      id: worker.id,
      name: `${worker.name} ${worker.surname}`,
      project: {
        id: project.projectId,
        title: project.projectTitle
      },
      visits: workerVisits
    });
    setShowVisitsModal(true);
  }, [allVisits]);

  // Filter workers by search term and status
  const filteredWorkers = useMemo(() => {
    return workers.map(worker => {
      // Filter projects based on all criteria
      let filteredProjects = worker.projects;

      // Status filter
      if (statusFilter === 'active') {
        filteredProjects = filteredProjects.filter(p => p.status === 'active');
      } else if (statusFilter === 'inactive') {
        filteredProjects = filteredProjects.filter(p => p.status !== 'active');
      } else if (statusFilter === 'incomplete') {
        // For incomplete filter, keep all projects but we'll check tasks later
        filteredProjects = filteredProjects;
      }

      // Primer/Backup filter
      if (primerFilter === 'primer') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === true);
      } else if (primerFilter === 'backup') {
        filteredProjects = filteredProjects.filter(p => p.isPrimer === false);
      }

      // Blinded/Unblinded filter
      if (blindedFilter === 'blinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === true);
      } else if (blindedFilter === 'unblinded') {
        filteredProjects = filteredProjects.filter(p => p.isBlinded === false);
      }

      // Hide finished projects
      if (hideFinished) {
        filteredProjects = filteredProjects.filter(p => !p.isFinished);
      }

      // Search filter
      if (searchTerm) {
        // Alanlar her zaman string değil (sayısal proje kodu, telefon vb.), bu
        // yüzden dönüşüm toSearchText üzerinden yapılır.
        const matchesWorker = matchesSearchTerm(
          searchTerm,
          `${worker.name} ${worker.surname}`,
          worker.email,
          worker.phone
        );

        if (!matchesWorker) {
          // If worker doesn't match, filter projects by search term
          filteredProjects = filteredProjects.filter(p =>
            matchesSearchTerm(searchTerm, p.projectTitle, p.centerName)
          );
        }
      }

      // Incomplete tasks filter
      if (statusFilter === 'incomplete') {
        const hasIncompleteTasks = incompleteTasks.byWorker[worker.id] && incompleteTasks.byWorker[worker.id].length > 0;
        if (!hasIncompleteTasks) {
          return null; // Exclude worker if no incomplete tasks
        }
      }

      // Return worker with filtered projects, or null if no projects match
      if (filteredProjects.length === 0) {
        return null;
      }

      return {
        ...worker,
        projects: filteredProjects
      };
    }).filter(Boolean); // Remove null entries
  }, [workers, searchTerm, statusFilter, primerFilter, blindedFilter, hideFinished, incompleteTasks]);

  // Download Excel
  const downloadExcel = () => {
    const dataToExport = filteredWorkers.flatMap(worker => 
      worker.projects.map(project => ({
        'SMO': selectedSMO ? `${selectedSMO.name} ${selectedSMO.surname}` : '',
        'Çalışan Adı': `${worker.name} ${worker.surname}`,
        'E-posta': worker.email || '-',
        'Telefon': worker.phone || '-',
        'Merkez': project.centerName || '-',
        'Merkezden Ayrılma': project.isArchived ? formatUnassignedDate(project) : '',
        'Proje': project.projectTitle,
        'Rol': project.role,
        'Durum': project.status === 'active' ? 'Aktif' : 'Pasif',
        'Tip': project.isPrimer ? 'Primer' : 'Backup',
        'Kör Durumu': project.isBlinded ? 'Blinded' : 'Unblinded',
        'Atanma Tarihi': project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                        (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Çalışanlar');
    
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'Tüm_SMO';
    XLSX.writeFile(workbook, `${smoName}_Çalışan_Listesi.xlsx`);
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  const downloadCapaExcel = () => {
    if (!section1Summary) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const wb = XLSX.utils.book_new();

    // Sheet 1: Operational Summary
    const summaryData = [
      { 'Metrik': 'Toplam Vizit', 'Değer': section1Summary.total },
      { 'Metrik': 'Ev Vizitleri', 'Değer': section1Summary.homeCount },
      { 'Metrik': 'Hastane Vizitleri', 'Değer': section1Summary.siteCount },
      { 'Metrik': 'Sorunsuz Vizit Oranı (%)', 'Değer': section1Summary.qualityRate },
      { 'Metrik': 'SLA Karşılanma Oranı (%)', 'Değer': section1Summary.slaRate },
      { 'Metrik': 'Dokümantasyon Tamlığı (%)', 'Değer': section1Summary.docRate },
      { 'Metrik': 'Toplam KM', 'Değer': section1Summary.totalKm },
      { 'Metrik': 'Ort. Döngü Süresi - Ev (gün)', 'Değer': section1Summary.avgCycleHome ?? '-' },
      { 'Metrik': 'Ort. Döngü Süresi - Hastane (gün)', 'Değer': section1Summary.avgCycleSite ?? '-' },
      { 'Metrik': 'Sorunlu Vizit', 'Değer': section1Summary.issueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': section1Summary.missedCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': section1Summary.deviationCount },
      { 'Metrik': 'CAPA Açılan', 'Değer': section1Summary.capaCount },
      { 'Metrik': 'Screen Fail', 'Değer': section1Summary.screenFail },
      { 'Metrik': 'Drop Patient', 'Değer': section1Summary.dropPatient },
      { 'Metrik': 'AE/SAE', 'Değer': section1Summary.aeSaeCount },
      { 'Metrik': 'DCF', 'Değer': section1Summary.dcfCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': section1Summary.overdueCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': section1Summary.missingProcessEnd },
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Genel Özet');

    // Sheet 2: Coordinator Breakdown
    if (capaDashboard) {
      const coordData = capaDashboard.coordinators.map(c => ({
        'Koordinatör': c.name,
        'Ev Viziti': c.totalVisits,
        'Toplam Sorun': c.totalIssues,
        'Kaçırılan Vizit': c.missedVisits,
        'AE/SAE Alınan': c.aeSaeCount,
        'DCF Oluşturulan': c.dcfCreatedCount,
        'Sorun Oranı (%)': c.totalVisits > 0 ? Math.round(c.totalIssues / c.totalVisits * 100) : 0,
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(coordData), 'Koordinatör Dağılımı');
    }

    XLSX.writeFile(wb, `${smoName}_Operasyonel_Ozet.xlsx`);
    toast.success('Operasyonel Özet Excel indirildi');
  };

  const downloadHastaneKpiExcel = () => {
    if (!section2Summary) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const data = [
      { 'Metrik': 'Toplam Vizit', 'Değer': section2Summary.total },
      { 'Metrik': 'Hastane Viziti', 'Değer': section2Summary.siteCount },
      { 'Metrik': 'Ev Viziti', 'Değer': section2Summary.homeCount },
      { 'Metrik': 'Protokol Sapması', 'Değer': section2Summary.deviationCount },
      { 'Metrik': 'CAPA Açılan', 'Değer': section2Summary.capaCount },
      { 'Metrik': 'Screen Fail', 'Değer': section2Summary.screenFail },
      { 'Metrik': 'Drop Patient', 'Değer': section2Summary.dropPatient },
      { 'Metrik': 'AE/SAE', 'Değer': section2Summary.aeSaeCount },
      { 'Metrik': 'DCF', 'Değer': section2Summary.dcfCount },
      { 'Metrik': 'Gecikmiş Vizit', 'Değer': section2Summary.overdueCount },
      { 'Metrik': 'Kaçırılan Vizit', 'Değer': section2Summary.missedCount },
      { 'Metrik': 'Süreç Bitiş Tarihi Eksik', 'Değer': section2Summary.missingProcessEnd },
      { 'Metrik': 'Hasta Çıkış Oranı (%)', 'Değer': section2Summary.siteCount > 0 ? Math.round(section2Summary.dropPatient / section2Summary.siteCount * 100) : 0 },
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Kalite & Risk');
    XLSX.writeFile(wb, `${smoName}_Kalite_Risk.xlsx`);
    toast.success('Kalite & Risk Excel indirildi');
  };

  const downloadEmployeeStatsExcel = () => {
    if (!employeeStats) return;
    const smoName = selectedSMO ? `${selectedSMO.name}_${selectedSMO.surname}` : 'SMO';
    const data = employeeStats.stats.map(emp => ({
      'Saha Koordinatörü': emp.name,
      'Toplam Vizit': emp.totalVisits,
      'Ev Vizitleri': emp.homeVisits,
      'Hastane Vizitleri': emp.siteVisits,
      'Toplam KM': emp.totalKm,
      'Ort. KM/Vizit': emp.avgKmPerVisit,
      'eCRF %': emp.ecrfPct,
      'Kaynak %': emp.sourcePct,
      'Sapma Sayısı': emp.deviationCount,
      'CAPA Açılan Sayısı': emp.capaOpenedCount,
      'Tarama Başarısız Sayısı': emp.screenFailCount,
      'Hasta Çıkış Sayısı': emp.dropPatientCount,
      'SLA Karşılanma %': emp.slaMetPct,
      'Gecikme Sayısı': emp.overdueCount,
      'Sapma %': emp.deviationPct,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Çalışan Performans');
    XLSX.writeFile(wb, `${smoName}_Calisan_Performans.xlsx`);
    toast.success('Çalışan Performans Excel indirildi');
  };

  // ── Admin-only management: remove a worker from this SMO, or remove the SMO ──
  // confirmAction: { type: 'worker', worker } | { type: 'smo' } | null
  const [confirmAction, setConfirmAction] = useState(null);
  const [removing, setRemoving] = useState(false);

  // Remove an employee from the selected SMO by deleting their assignment
  // entries for this SMO's centers from both the Projects and Employees docs.
  const removeWorkerFromSMO = useCallback(async (worker) => {
    if (!selectedSMO || !worker) return;
    const centerIds = selectedSMO.centers.map(c => c.id);
    setRemoving(true);
    try {
      const projectsSnap = await getDocs(collection(db, 'Projects'));
      const updates = [];

      projectsSnap.docs.forEach(pd => {
        // Respect project-scoped (delegated) views, though admins won't have one
        if (selectedSMO.restrictToProjectId && pd.id !== selectedSMO.restrictToProjectId) return;
        const data = pd.data();
        const emps = data.employees || [];
        const filtered = emps.filter(
          e => !(e.id === worker.id && centerIds.includes(e.centerId))
        );
        if (filtered.length !== emps.length) {
          updates.push(updateDoc(doc(db, 'Projects', pd.id), { employees: filtered }));
        }
      });

      // Mirror the removal in the employee's own projects array
      const empRef = doc(db, 'Employees', worker.id);
      const empDoc = await getDoc(empRef);
      if (empDoc.exists()) {
        const projects = empDoc.data().projects || [];
        const filteredProjects = projects.filter(p => {
          if (selectedSMO.restrictToProjectId && p.id !== selectedSMO.restrictToProjectId) return true;
          return !centerIds.includes(p.centerId);
        });
        if (filteredProjects.length !== projects.length) {
          updates.push(updateDoc(empRef, { projects: filteredProjects }));
        }
      }

      await Promise.all(updates);
      toast.success(`${worker.name} ${worker.surname} bu SMO'dan çıkarıldı.`);
      setConfirmAction(null);
      await fetchWorkersForSMO();
    } catch (error) {
      console.error('Error removing worker from SMO:', error);
      toast.error('Çalışan çıkarılırken bir hata oluştu!');
    } finally {
      setRemoving(false);
    }
  }, [selectedSMO, fetchWorkersForSMO]);

  // Remove the SMO designation by clearing the SMO field on all of its centers.
  // The centers themselves and their project assignments are untouched.
  const removeSMO = useCallback(async () => {
    if (!selectedSMO) return;
    const centerIds = selectedSMO.centers.map(c => c.id);
    setRemoving(true);
    try {
      await Promise.all(
        centerIds.map(id => updateDoc(doc(db, 'Centers', id), { SMO: '' }))
      );
      toast.success(`${selectedSMO.name} ${selectedSMO.surname} SMO ataması ${centerIds.length} merkezden kaldırıldı.`);
      // Drop the removed SMO from the list and clear selection (no full reload)
      const removedId = selectedSMO.id;
      setSmoList(prev => prev.filter(s => s.id !== removedId));
      setConfirmAction(null);
      setSelectedSMO(null);
      setWorkers([]);
    } catch (error) {
      console.error('Error removing SMO:', error);
      toast.error('SMO kaldırılırken bir hata oluştu!');
    } finally {
      setRemoving(false);
    }
  }, [selectedSMO]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        {/* Header */}
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <div>
              <h1 className={styles.title}>
                <FaBuilding className={styles.headerIcon} />
                SMO Lead Raporları
              </h1>
              <div className={styles.breadcrumb}>Raporlar / SMO Lead Raporları</div>
            </div>
            {selectedSMO && workers.length > 0 && (
              <div className={styles.headerButtons}>
                {canManualEntry && (
                  <button 
                    className={`${styles.button} ${styles.manualEntryButton}`} 
                    onClick={() => setShowManualEntry(true)}
                  >
                    <FaEdit className={styles.icon} /> Manuel Veri Girişi
                  </button>
                )}
                <button 
                  className={`${styles.button} ${styles.refreshButton}`} 
                  onClick={fetchWorkersForSMO}
                  disabled={loadingWorkers}
                  title="Çalışan verilerini yenile"
                >
                  <FaSync className={`${styles.icon} ${loadingWorkers ? styles.spinning : ''}`} /> 
                  {loadingWorkers ? 'Yenileniyor...' : 'Yenile'}
                </button>
                <button 
                  className={`${styles.button} ${styles.downloadButton}`} 
                  onClick={downloadExcel}
                >
                  <FaDownload className={styles.icon} /> Tabloyu İndir
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Stats Cards */}
        <div className={styles.cardContainer}>
          <div className={`${styles.card} ${styles.cardTotal}`}>
            <div className={styles.cardContent}>
              <div className={styles.cardTitle}>Toplam SMO Sayısı</div>
              <div className={styles.cardValue}>{smoList.length}</div>
            </div>
            <FaUserTie className={styles.cardIcon} />
          </div>
          
          {selectedSMO && (
            <div className={`${styles.card} ${styles.cardActive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>SMO'ya Bağlı Merkez Sayısı</div>
                <div className={styles.cardValue}>
                  {selectedSMO.centerCount}
                  {selectedSMO.centers && (() => {
                    const primerCount = selectedSMO.centers.filter(c => c.isPrimerSmo).length;
                    const backupCount = selectedSMO.centers.filter(c => !c.isPrimerSmo).length;
                    if (primerCount > 0 && backupCount > 0) {
                      return (
                        <span style={{ 
                          fontSize: '0.6rem', 
                          marginLeft: '8px',
                          color: '#666',
                          fontWeight: 400
                        }}>
                          ({primerCount} Primer, {backupCount} Backup)
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>
              <FaBuilding className={styles.cardIcon} />
            </div>
          )}
          
          {selectedSMO && (
            <div className={`${styles.card} ${styles.cardInactive}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Bu SMO Altında Çalışan Sayısı</div>
                <div className={styles.cardValue}>{workers.length}</div>
              </div>
              <FaUsers className={styles.cardIcon} />
            </div>
          )}
        </div>

        {/* SMO Selection Grid — admins only */}
        {isAdmin && <div className={styles.smoSectionTitle}>
          <FaUserTie style={{ marginRight: '0.5rem' }} />
          SMO Listesi
          <span className={styles.smoSectionSubtitle}>
            Detayları görmek için bir SMO seçiniz
          </span>
        </div>}
        
        {isAdmin && loadingSMOs ? (
          <div className={styles.loadingContainer}>
            <svg 
              style={{ 
                animation: 'spin 1s linear infinite', 
                height: '2.5rem', 
                width: '2.5rem',
                margin: '0 auto'
              }} 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }}></circle>
              <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" style={{ opacity: 0.75 }}></path>
            </svg>
            <p style={{ marginTop: '1rem' }}>SMO listesi yükleniyor...</p>
          </div>
        ) : (() => {
          // Build the list of selectable SMO views.
          // - Admin: full smoList.
          // - Non-admin: own SMO (if any) + delegated backup-SMO-Lead views.
          let visibleList = [];
          if (isAdmin) {
            visibleList = smoList.map(s => ({ ...s, viewKey: s.id }));
          } else {
            const ownSmo = smoList.find(s => s.id === employeeId);
            if (ownSmo) visibleList.push({ ...ownSmo, viewKey: ownSmo.id });
            visibleList = visibleList.concat(delegatedSmoViews, centerBackupViews);
          }
          // Hide picker entirely when there's nothing to pick OR when a
          // non-admin has exactly one entry (auto-selected, no need to show).
          if (visibleList.length === 0) {
            return isAdmin ? (
              <div className={styles.emptyState}>
                <FaUserTie className={styles.emptyStateIcon} />
                <p className={styles.emptyStateText}>Henüz hiçbir merkeze SMO atanmamış.</p>
              </div>
            ) : null;
          }
          if (!isAdmin && visibleList.length === 1) return null;
          const currentKey = selectedSMO?.viewKey || selectedSMO?.id;
          return (
            <div className={styles.smoGrid}>
              {visibleList.map(smo => {
                const key = smo.viewKey || smo.id;
                const isProjectBackup = !!smo.delegatedFromBackup;
                const isCenterBackup = !!smo.delegatedFromCenterBackup;
                const isDelegated = isProjectBackup || isCenterBackup;
                return (
                  <div
                    key={key}
                    className={`${styles.smoCard} ${currentKey === key ? styles.smoCardSelected : ''}`}
                    onClick={() => handleSMOClick(smo)}
                    title={
                      isProjectBackup
                        ? `Yedek görünümü: ${smo.restrictToProjectTitle || ''}`
                        : isCenterBackup
                        ? `Merkez yedeği görünümü: ${smo.centerCount} merkez`
                        : undefined
                    }
                  >
                    <div className={styles.smoCardAvatar}>
                      <FaUserTie />
                    </div>
                    <div className={styles.smoCardInfo}>
                      <div className={styles.smoCardName}>
                        {smo.name} {smo.surname}
                        {isDelegated && (
                          <span
                            style={{
                              marginLeft: 8,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: '#fef5e7',
                              color: '#7b341e',
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                              verticalAlign: 'middle',
                            }}
                          >
                            {isCenterBackup ? 'Merkez Yedeği' : 'Yedek'}
                          </span>
                        )}
                      </div>
                      {isProjectBackup ? (
                        <div className={styles.smoCardDetail} style={{ color: '#c05621' }}>
                          <FaProjectDiagram style={{ fontSize: '0.7rem' }} />
                          {smo.restrictToProjectTitle || 'Proje'}
                        </div>
                      ) : (
                        <div className={styles.smoCardDetail}>
                          <FaBuilding style={{ fontSize: '0.7rem' }} />
                          {smo.centerCount} Merkez
                        </div>
                      )}
                      {smo.email && (
                        <div className={styles.smoCardDetail}>{smo.email}</div>
                      )}
                    </div>
                    {currentKey === key && <div className={styles.smoCardCheck}>✓</div>}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Workers Section - shown when an SMO is selected */}
        {selectedSMO && (
          <div className={styles.workersSection}>
            <div className={styles.workersSectionHeader}>
              <h2 className={styles.workersSectionTitle}>
                <FaUsers style={{ marginRight: '0.5rem' }} />
                {selectedSMO.name} {selectedSMO.surname} - Çalışanlar
                {selectedSMO.delegatedFromBackup && (
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: '#fef5e7',
                      color: '#7b341e',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      verticalAlign: 'middle',
                    }}
                    title="Bu görünüm yedek SMO Lead atamanız nedeniyle yalnızca seçili proje ile sınırlıdır"
                  >
                    Yedek görünüm · {selectedSMO.restrictToProjectTitle}
                  </span>
                )}
                {selectedSMO.delegatedFromCenterBackup && (
                  <span
                    style={{
                      marginLeft: 10,
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: '#fef5e7',
                      color: '#7b341e',
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                      verticalAlign: 'middle',
                    }}
                    title="Bu görünüm, yedek SMO olarak atandığınız merkezler ile sınırlıdır"
                  >
                    Merkez yedeği görünümü · {selectedSMO.centerCount} merkez
                  </span>
                )}
              </h2>

              {/* Admin-only: remove this SMO (clears the SMO assignment on its centers) */}
              {canRemoveSMO && !selectedSMO.delegatedFromBackup && !selectedSMO.delegatedFromCenterBackup && (
                <button
                  className={styles.removeSmoButton}
                  onClick={() => setConfirmAction({ type: 'smo' })}
                  title="Bu SMO atamasını merkezlerinden kaldır"
                >
                  <FaTrashAlt /> SMO'yu Kaldır
                </button>
              )}

              {/* Centers badges - City-based collapsible sections */}
              <div className={styles.citySection}>
                {(() => {
                  // Şehirlere göre grupla
                  const cityGroups = {};
                  smoCenters.forEach(center => {
                    const city = center.city || 'Bilinmeyen İl';
                    if (!cityGroups[city]) {
                      cityGroups[city] = [];
                    }
                    cityGroups[city].push(center);
                  });

                  // 0 merkezli şehirleri filtrele
                  const cities = Object.keys(cityGroups)
                    .filter(city => cityGroups[city].length > 0)
                    .sort((a, b) => a.localeCompare(b, 'tr'));

                  return cities.map(city => {
                    const cityKey = `${selectedSMO.id}-${city}`;
                    const centersInCity = cityGroups[city];
                    const centerCount = centersInCity.length;
                    
                    // Tüm şehirler default kapalı gelsin
                    const isCollapsed = collapsedCities[cityKey] ?? true;
                    
                    // İlçelere göre grupla (10'dan fazla merkez varsa)
                    const groupByDistrict = centerCount > 10;
                    const districtGroups = {};
                    
                    if (groupByDistrict) {
                      centersInCity.forEach(center => {
                        const district = center.district || 'Bilinmeyen İlçe';
                        if (!districtGroups[district]) {
                          districtGroups[district] = [];
                        }
                        districtGroups[district].push(center);
                      });
                    }
                    
                    return (
                      <div key={city} className={styles.cityGroup}>
                        <div
                          className={styles.cityBadgeClickable}
                          onClick={() => setCollapsedCities(prev => ({
                            ...prev,
                            [cityKey]: !(prev[cityKey] ?? true)
                          }))}
                        >
                          <FaMapMarkerAlt style={{ fontSize: '0.8rem' }} />
                          <span className={styles.cityName}>{city}</span>
                          <span className={styles.centerCount}>
                            ({centerCount} Merkez{groupByDistrict ? ` · ${Object.keys(districtGroups).length} İlçe` : ''})
                          </span>
                          {isCollapsed ? (
                            <FaChevronDown className={styles.collapseIcon} />
                          ) : (
                            <FaChevronUp className={styles.collapseIcon} />
                          )}
                        </div>
                        
                        {!isCollapsed && (
                          <div className={styles.cityCenters}>
                            {groupByDistrict ? (
                              // İlçe bazlı gruplandırma
                              Object.entries(districtGroups)
                                .sort((a, b) => a[0].localeCompare(b[0], 'tr'))
                                .map(([district, centers]) => {
                                  const districtKey = `${cityKey}-${district}`;
                                  const isDistrictCollapsed = collapsedCities[districtKey] ?? true;
                                  
                                  return (
                                    <div key={district} className={styles.districtGroup}>
                                      <div
                                        className={styles.districtBadgeClickable}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCollapsedCities(prev => ({
                                            ...prev,
                                            [districtKey]: !(prev[districtKey] ?? true)
                                          }));
                                        }}
                                      >
                                        <span className={styles.districtName}>
                                          {district}
                                        </span>
                                        <span className={styles.districtCenterCount}>
                                          {centers.length} merkez
                                        </span>
                                        {isDistrictCollapsed ? (
                                          <FaChevronDown className={styles.districtCollapseIcon} />
                                        ) : (
                                          <FaChevronUp className={styles.districtCollapseIcon} />
                                        )}
                                      </div>
                                      
                                      {!isDistrictCollapsed && (
                                        <div className={styles.districtCenters}>
                                          {centers.map(center => (
                                            <span 
                                              key={center.id} 
                                              className={styles.centerBadge}
                                              style={{
                                                background: center.isPrimerSmo ? '#e7f3ff' : '#fff5e6',
                                                borderColor: center.isPrimerSmo ? '#4a90e2' : '#f39c12',
                                                color: center.isPrimerSmo ? '#1e5a8e' : '#a67c00'
                                              }}
                                            >
                                              <FaBuilding style={{ fontSize: '0.65rem' }} />
                                              {center.name}
                                              <span 
                                                style={{ 
                                                  marginLeft: '6px',
                                                  fontSize: '0.65rem',
                                                  fontWeight: 700,
                                                  padding: '2px 6px',
                                                  borderRadius: '4px',
                                                  background: center.isPrimerSmo ? '#4a90e2' : '#f39c12',
                                                  color: 'white'
                                                }}
                                              >
                                                {center.isPrimerSmo ? 'P' : 'B'}
                                              </span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })
                            ) : (
                              // Direkt merkez listesi (10'dan az merkez)
                              centersInCity.map(center => (
                                <span 
                                  key={center.id} 
                                  className={styles.centerBadge}
                                  style={{
                                    background: center.isPrimerSmo ? '#e7f3ff' : '#fff5e6',
                                    borderColor: center.isPrimerSmo ? '#4a90e2' : '#f39c12',
                                    color: center.isPrimerSmo ? '#1e5a8e' : '#a67c00'
                                  }}
                                >
                                  <FaBuilding style={{ fontSize: '0.65rem' }} />
                                  {center.name}
                                  <span 
                                    style={{ 
                                      marginLeft: '6px',
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      padding: '2px 6px',
                                      borderRadius: '4px',
                                      background: center.isPrimerSmo ? '#4a90e2' : '#f39c12',
                                      color: 'white'
                                    }}
                                  >
                                    {center.isPrimerSmo ? 'P' : 'B'}
                                  </span>
                                </span>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            {/* Search and Filter */}
            {workers.length > 0 && (
              <div className={styles.searchContainer}>
                <div className={styles.searchBar}>
                  <FaSearch className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Çalışan, proje veya merkez ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                  {searchTerm && (
                    <button className={styles.searchClear} onClick={() => setSearchTerm('')}>
                      <FaTimes />
                    </button>
                  )}
                </div>
                
                {/* Modern Filter Section */}
                <div className={styles.modernFilterSection}>
                  {/* Status Filter Dropdown */}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Durum</label>
                    <select 
                      className={styles.filterSelect}
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">Tümü ({workers.length})</option>
                      <option value="active">Aktif ({workers.filter(w => w.projects.some(p => p.status === 'active')).length})</option>
                      <option value="inactive">Pasif ({workers.filter(w => w.projects.some(p => p.status !== 'active')).length})</option>
                      <option value="incomplete">Tamamlanmamış ({Object.keys(incompleteTasks.byWorker).length})</option>
                    </select>
                  </div>

                  {/* Primer/Backup Filter Dropdown */}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Tip</label>
                    <select 
                      className={styles.filterSelect}
                      value={primerFilter}
                      onChange={(e) => setPrimerFilter(e.target.value)}
                    >
                      <option value="all">Tüm Tipler ({workers.length})</option>
                      <option value="primer">Primer ({workers.filter(w => w.projects.some(p => p.isPrimer === true)).length})</option>
                      <option value="backup">Backup ({workers.filter(w => w.projects.some(p => p.isPrimer === false)).length})</option>
                    </select>
                  </div>

                  {/* Blinded/Unblinded Filter Dropdown */}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Kör Durumu</label>
                    <select 
                      className={styles.filterSelect}
                      value={blindedFilter}
                      onChange={(e) => setBlindedFilter(e.target.value)}
                    >
                      <option value="all">Tümü ({workers.length})</option>
                      <option value="blinded">Blinded ({workers.filter(w => w.projects.some(p => p.isBlinded === true)).length})</option>
                      <option value="unblinded">Unblinded ({workers.filter(w => w.projects.some(p => p.isBlinded === false)).length})</option>
                    </select>
                  </div>

                  {/* Finished projects toggle */}
                  <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Bitmiş Projeler</label>
                    <button
                      type="button"
                      className={`${styles.finishedToggle} ${hideFinished ? styles.finishedToggleActive : ''}`}
                      onClick={() => setHideFinished(v => !v)}
                      title="Bitmiş olarak işaretlenen projeleri listeden gizle"
                    >
                      <FaFlagCheckered />
                      {hideFinished ? 'Bitmiş projeler gizli' : 'Bitmiş projeleri gizle'}
                    </button>
                  </div>

                  {/* Active Filters Display (Chips) */}
                  {(statusFilter !== 'all' || primerFilter !== 'all' || blindedFilter !== 'all' || hideFinished) && (
                    <div className={styles.activeFiltersChips}>
                      {statusFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipStatus}`}>
                          {statusFilter === 'active' ? 'Aktif' : statusFilter === 'inactive' ? 'Pasif' : 'Tamamlanmamış'}
                          <button onClick={() => setStatusFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {primerFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipPrimer}`}>
                          {primerFilter === 'primer' ? 'Primer' : 'Backup'}
                          <button onClick={() => setPrimerFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {blindedFilter !== 'all' && (
                        <span className={`${styles.filterChip} ${styles.filterChipBlinded}`}>
                          {blindedFilter === 'blinded' ? 'Blinded' : 'Unblinded'}
                          <button onClick={() => setBlindedFilter('all')} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      {hideFinished && (
                        <span className={`${styles.filterChip} ${styles.filterChipStatus}`}>
                          Bitmiş projeler gizli
                          <button onClick={() => setHideFinished(false)} className={styles.chipRemove}>×</button>
                        </span>
                      )}
                      <button
                        className={styles.clearAllFilters}
                        onClick={() => {
                          setStatusFilter('all');
                          setPrimerFilter('all');
                          setBlindedFilter('all');
                          setHideFinished(false);
                        }}
                      >
                        Tümünü Temizle
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {loadingWorkers ? (
              <div className={styles.loadingContainer}>
                <svg 
                  style={{ 
                    animation: 'spin 1s linear infinite', 
                    height: '2rem', 
                    width: '2rem',
                    margin: '0 auto'
                  }} 
                  xmlns="http://www.w3.org/2000/svg" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }}></circle>
                  <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" style={{ opacity: 0.75 }}></path>
                </svg>
                <p style={{ marginTop: '0.75rem' }}>Çalışanlar yükleniyor...</p>
              </div>
            ) : filteredWorkers.length > 0 ? (
              <section className={styles.tableSection}>
                <div className={styles.tableWrapper}>
                  <div className={styles.tableScrollContainer}>
                    {showLeftScroll && (
                      <button
                        className={`${styles.scrollButton} ${styles.scrollLeft}`}
                        onClick={() => scrollTable('left')}
                      >
                        <FaChevronLeft />
                      </button>
                    )}
                    {showRightScroll && (
                      <button
                        className={`${styles.scrollButton} ${styles.scrollRight}`}
                        onClick={() => scrollTable('right')}
                      >
                        <FaChevronRight />
                      </button>
                    )}
                    <div
                      ref={tableContainerRef}
                      className={styles.tableContainer}
                      onScroll={handleScroll}
                    >
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Çalışan</th>
                            <th>E-posta</th>
                            <th>Telefon</th>
                            <th>Proje Sayısı</th>
                            <th>Merkez(ler)</th>
                            {canRemoveSMO && <th style={{ width: '60px', textAlign: 'center' }}>İşlem</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWorkers.map((worker) => (
                            <React.Fragment key={worker.id}>
                              {/* Main worker row */}
                              <tr
                                className={`${styles.workerRow} ${expandedWorkers[worker.id] ? styles.workerRowExpanded : ''}`}
                                onClick={() => toggleWorkerExpand(worker.id)}
                              >
                                <td>
                                  <span className={styles.expandIcon}>
                                    {expandedWorkers[worker.id] ? <FaChevronUp /> : <FaChevronDown />}
                                  </span>
                                </td>
                                <td className={styles.workerName}>
                                  <span className={styles.workerNameCell}>
                                    <span className={styles.workerNameText}>
                                      {worker.name} {worker.surname}
                                    </span>
                                    {/* Satır tıklaması projeleri açıyor; belge butonu onu tetiklemesin */}
                                    <button
                                      type="button"
                                      className={styles.documentsButton}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setDocumentsEmployee(worker);
                                      }}
                                      title={`${worker.name} ${worker.surname} - CV/GCP/IATA belgelerini görüntüle`}
                                      aria-label="CV/GCP/IATA belgelerini görüntüle"
                                    >
                                      <FaFolderOpen />
                                    </button>
                                  </span>
                                </td>
                                <td>{worker.email || '-'}</td>
                                <td>{worker.phone || '-'}</td>
                                <td>
                                  <span className={styles.projectCountBadge}>
                                    {worker.projects.length}
                                  </span>
                                </td>
                                {(() => {
                                  // Merkez adları uzun; hücre tek satırda kısalır ve
                                  // tam liste tooltip'te kalır, böylece sağdaki
                                  // sütunlar ekran dışına taşmaz.
                                  const centerNames = [
                                    ...new Set(worker.projects.map((p) => p.centerName).filter(Boolean)),
                                  ];
                                  const centersText = centerNames.join(', ') || '-';
                                  return (
                                    <td className={styles.centersCell} title={centersText}>
                                      {centersText}
                                    </td>
                                  );
                                })()}
{canRemoveSMO && (
                                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                    <button
                                      className={styles.removeWorkerButton}
                                      onClick={() => setConfirmAction({ type: 'worker', worker })}
                                      title="Bu çalışanı bu SMO'dan çıkar"
                                    >
                                      <FaUserMinus />
                                    </button>
                                  </td>
                                )}
                              </tr>
                              
                              {/* Expanded project/visit rows */}
                              {expandedWorkers[worker.id] && (
                                <tr className={styles.expandedRow}>
                                  <td colSpan={canRemoveSMO ? 7 : 6}>
                                    <div className={styles.expandedContent}>
                                      {/* Show incomplete tasks if filter is set to incomplete */}
                                      {statusFilter === 'incomplete' && incompleteTasks.byWorker[worker.id] && (
                                        <div className={styles.incompleteTasksSection}>
                                          <h4 className={styles.incompleteTasksTitle}>
                                            <FaClipboardList style={{ marginRight: '0.5rem' }} />
                                            Tamamlanmamış Görevler ({incompleteTasks.byWorker[worker.id].length})
                                          </h4>
                                          <div className={styles.incompleteTasksList}>
                                            {incompleteTasks.byWorker[worker.id].map((task, taskIdx) => (
                                              <div key={taskIdx} className={styles.incompleteTaskItem}>
                                                <div className={styles.taskHeader}>
                                                  <span className={`${styles.taskTypeBadge} ${task.type === 'visit' ? styles.taskTypeVisit : styles.taskTypeJob}`}>
                                                    {task.type === 'visit' ? 'Vizit' : 'Görev'}
                                                  </span>
                                                  <span className={styles.taskDate}>
                                                    {task.date ? new Date(task.date).toLocaleDateString('tr-TR') : '-'}
                                                  </span>
                                                </div>
                                                <div className={styles.taskContent}>
                                                  {task.type === 'visit' ? (
                                                    <>
                                                      <strong>{task.patientId || 'Hasta'}</strong>
                                                      <br />
                                                      <small>{task.visitType === 'home' ? 'Ev Viziti' : 'Hastane Viziti'}</small>
                                                    </>
                                                  ) : (
                                                    <>
                                                      <strong>{task.title}</strong>
                                                      {task.description && <><br /><small>{task.description}</small></>}
                                                    </>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Show projects if not filtering by incomplete tasks */}
                                      {statusFilter !== 'incomplete' && (
                                        <div className={styles.projectsGrid}>
                                          {worker.projects.map((project, idx) => (
                                            <div key={`${project.projectId}-${idx}`} className={`${styles.projectCard} ${project.isFinished ? styles.projectCardFinished : ''}`}>
                                              {project.isFinished && (
                                                <span className={styles.finishedRibbon}>
                                                  <FaFlagCheckered style={{ marginRight: 4 }} />
                                                  Bitti
                                                </span>
                                              )}
                                              <div className={styles.projectCardHeader}>
                                                <FaProjectDiagram className={styles.projectCardIcon} />
                                                <span className={styles.projectCardTitle}>{project.projectTitle}</span>
                                                <span className={`${styles.statusBadge} ${project.status === 'active' ? styles.statusActive : styles.statusInactive}`}>
                                                  {project.status === 'active' ? 'Aktif' : 'Pasif'}
                                                </span>
                                              </div>
                                              <div className={styles.projectCardDetails}>
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Rol:</span>
                                                  <span className={styles.detailValue}>{project.role || '-'}</span>
                                                </div>
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Merkez:</span>
                                                  <span className={styles.detailValue}>
                                                    {project.centerName || '-'}
                                                    {project.isArchived && (
                                                      <span className={styles.archivedBadge}>
                                                        {getArchivedAssignmentLabel(project)}
                                                      </span>
                                                    )}
                                                  </span>
                                                </div>
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Tip:</span>
                                                  <span className={styles.detailValue}>
                                                    {project.isPrimer ? 'Primer' : 'Backup'}
                                                  </span>
                                                </div>
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Kör:</span>
                                                  <span className={styles.detailValue}>
                                                    {project.isBlinded ? 'Blinded' : 'Unblinded'}
                                                  </span>
                                                </div>
                                                {canRemoveSMO && (
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Tarife:</span>
                                                  <span className={styles.detailValue}>
                                                    {project.rate?.type ? `${project.rate.type} - ${project.rate.value || ''}` : '-'}
                                                  </span>
                                                </div>
                                                )}
                                                <div className={styles.projectCardDetail}>
                                                  <span className={styles.detailLabel}>Atanma Tarihi:</span>
                                                  <span className={styles.detailValue}>
                                                    {project.assignedAt?.toDate?.()?.toLocaleDateString('tr-TR') || 
                                                     (project.assignedAt instanceof Date ? project.assignedAt.toLocaleDateString('tr-TR') : '-')}
                                                  </span>
                                                </div>
                                              </div>
                                              <button
                                                className={styles.viewVisitsButton}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleViewVisits(worker, project);
                                                }}
                                              >
                                                <FaEye style={{ marginRight: '0.375rem' }} />
                                                Visitleri Görüntüle
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            ) : (
              <div className={styles.emptyState}>
                <FaUsers className={styles.emptyStateIcon} />
                <p className={styles.emptyStateText}>
                  {searchTerm ? 'Arama kriterine uygun çalışan bulunamadı.' : 'Bu SMO altında çalışan bulunmamaktadır.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SECTION 1: Operasyonel Genel Bakış
            Story: "How is this SMO performing overall?"
            ══════════════════════════════════════════════════════════════ */}
        {selectedSMO && (
          <div className={styles.capaDashboardSection}>
            <div className={styles.smoSectionTitle}>
              <FaChartBar style={{ marginRight: '0.5rem' }} />
              Operasyonel Genel Bakış
              {loadingVisits && <span className={styles.capaDashboardSubtitle}>Yükleniyor...</span>}
              {!loadingVisits && section1Summary && <span className={styles.capaDashboardSubtitle}>{section1Summary.total} vizit analiz edildi ({section1Summary.homeCount} ev / {section1Summary.siteCount} hastane)</span>}
              {!loadingVisits && section1Summary && (
                <button className={styles.sectionDownloadButton} onClick={downloadCapaExcel} title="Excel olarak indir">
                  <FaDownload /> Excel
                </button>
              )}
              <button className={styles.sectionToggleButton} onClick={() => toggleSection('issueTracking')} title={expandedSections.issueTracking ? 'Daralt' : 'Genişlet'}>
                {expandedSections.issueTracking ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            {/* Date Range Filter */}
            <div className={styles.dateFilterRow}>
              <label className={styles.dateFilterLabel}>Tarih Aralığı:</label>
              <input type="date" className={styles.dateFilterInput} value={s1DateStart} onChange={e => setS1DateStart(e.target.value)} />
              <span className={styles.dateFilterSep}>—</span>
              <input type="date" className={styles.dateFilterInput} value={s1DateEnd} onChange={e => setS1DateEnd(e.target.value)} />
              <button className={styles.dateFilterBtn} onClick={() => resetDateFilter(1)} title="Bu ay">Bu Ay</button>
              <button className={styles.dateFilterBtn} onClick={() => setAllTime(1)} title="Tüm zamanlar">Tümü</button>
            </div>

            {!loadingVisits && !section1Summary && (
              <div className={styles.emptyState}>
                <FaExclamationTriangle className={styles.emptyStateIcon} style={{ color: '#a0aec0' }} />
                <p className={styles.emptyStateText}>Seçilen tarih aralığında vizit bulunamadı.</p>
              </div>
            )}

            {!loadingVisits && section1Summary && expandedSections.issueTracking && (
              <>
                {/* ── Hero Metric Cards ── */}
                <div className={styles.storyHeroCards}>
                  <div className={`${styles.storyHeroCard} ${styles.storyHeroBlue}`}>
                    <div className={styles.storyHeroValue}>{section1Summary.total}</div>
                    <div className={styles.storyHeroLabel}>Toplam Vizit</div>
                    <div className={styles.storyHeroSub}>{section1Summary.homeCount} ev · {section1Summary.siteCount} hastane</div>
                  </div>
                  <div
                    className={`${styles.storyHeroCard} ${section1Summary.qualityRate >= 90 ? styles.storyHeroGreen : section1Summary.qualityRate >= 70 ? styles.storyHeroYellow : styles.storyHeroRed} ${section1Summary.issueCount > 0 ? styles.issueClickable : ''}`}
                    onClick={section1Summary.issueCount > 0 ? () => openIssueModal('Sorunlu Vizitler', s1Visits, 'issues') : undefined}
                    title={section1Summary.issueCount > 0 ? 'Sorunlu vizitleri görüntüleyin' : undefined}
                  >
                    <div className={styles.storyHeroValue}>%{section1Summary.qualityRate}</div>
                    <div className={styles.storyHeroLabel}>Sorunsuz Vizit Oranı</div>
                    <div className={styles.storyHeroSub}>{section1Summary.issueCount} sorunlu vizit</div>
                  </div>
                  <div
                    className={`${styles.storyHeroCard} ${section1Summary.slaRate >= 80 ? styles.storyHeroGreen : section1Summary.slaRate >= 50 ? styles.storyHeroYellow : styles.storyHeroRed} ${section1Summary.overdueCount > 0 ? styles.issueClickable : ''}`}
                    onClick={section1Summary.overdueCount > 0 ? () => openIssueModal('SLA Gecikmiş Vizitler', s1Visits, 'overdue') : undefined}
                    title={section1Summary.overdueCount > 0 ? 'Gecikmiş vizitleri görüntüleyin' : undefined}
                  >
                    <div className={styles.storyHeroValue}>%{section1Summary.slaRate}</div>
                    <div className={styles.storyHeroLabel}>SLA Karşılanma</div>
                    <div className={styles.storyHeroSub}>{section1Summary.overdueCount} gecikmiş vizit</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${section1Summary.docRate >= 80 ? styles.storyHeroGreen : section1Summary.docRate >= 50 ? styles.storyHeroYellow : styles.storyHeroRed}`}>
                    <div className={styles.storyHeroValue}>%{section1Summary.docRate}</div>
                    <div className={styles.storyHeroLabel}>Dokümantasyon Tamlığı</div>
                    <div className={styles.storyHeroSub}>eCRF + Kaynak + İmza ortalaması</div>
                  </div>
                  <div className={`${styles.storyHeroCard} ${styles.storyHeroPurple}`}>
                    <div className={styles.storyHeroValue}>{section1Summary.totalKm > 0 ? section1Summary.totalKm.toLocaleString('tr-TR') : '—'}</div>
                    <div className={styles.storyHeroLabel}>Toplam KM</div>
                    <div className={styles.storyHeroSub}>Seyahat edilen mesafe</div>
                  </div>
                </div>

                {/* ── Alerts Strip ── */}
                {section1Summary.alerts.length > 0 && (
                  <div className={styles.storyAlerts}>
                    {section1Summary.alerts.map((alert, i) => {
                      const clickable = !!alert.metric && filterVisitsByMetric(s1Visits, alert.metric).length > 0;
                      return (
                        <div
                          key={i}
                          className={`${styles.storyAlert} ${alert.type === 'danger' ? styles.storyAlertDanger : alert.type === 'warning' ? styles.storyAlertWarning : styles.storyAlertInfo} ${clickable ? styles.issueClickable : ''}`}
                          onClick={clickable ? () => openIssueModal(alert.text, s1Visits, alert.metric) : undefined}
                          title={clickable ? 'Detayları görüntülemek için tıklayın' : undefined}
                        >
                          <FaExclamationTriangle />
                          <span>{alert.text}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* ── Ev vs Hastane Karşılaştırma ── */}
                {section1Summary.comparisonChart.length > 0 && (
                  <div className={styles.storyComparisonRow}>
                    <div className={styles.capaChartCard} style={{ flex: 1 }}>
                      <h4 className={styles.capaChartTitle}>Ev vs Hastane Karşılaştırması</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={section1Summary.comparisonChart}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                          <YAxis unit="%" domain={[0, 100]} />
                          <Tooltip formatter={v => v + '%'} />
                          <Legend />
                          <Bar dataKey="Ev" fill="#4299e1" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="Hastane" fill="#9f7aea" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Cycle Time Summary Card */}
                    <div className={styles.capaChartCard} style={{ flex: 1 }}>
                      <h4 className={styles.capaChartTitle}>Döngü Süreleri & Süreç Özeti</h4>
                      <div className={styles.storyCycleGrid}>
                        <div className={styles.storyCycleTile}>
                          <div className={styles.storyCycleValue}>{section1Summary.avgCycleHome !== null ? `${section1Summary.avgCycleHome} gün` : '—'}</div>
                          <div className={styles.storyCycleLabel}>Ort. Döngü — Ev</div>
                        </div>
                        <div className={styles.storyCycleTile}>
                          <div className={styles.storyCycleValue}>{section1Summary.avgCycleSite !== null ? `${section1Summary.avgCycleSite} gün` : '—'}</div>
                          <div className={styles.storyCycleLabel}>Ort. Döngü — Hastane</div>
                        </div>
                        <div
                          className={`${styles.storyCycleTile} ${section1Summary.missingProcessEnd > 0 ? styles.issueClickable : ''}`}
                          onClick={section1Summary.missingProcessEnd > 0 ? () => openIssueModal('Süreç Bitiş Tarihi Eksik Vizitler', s1Visits, 'missingProcessEnd') : undefined}
                          title={section1Summary.missingProcessEnd > 0 ? 'Detayları görüntüleyin' : undefined}
                        >
                          <div className={styles.storyCycleValue} style={{ color: section1Summary.missingProcessEnd > 0 ? '#c05621' : '#276749' }}>{section1Summary.missingProcessEnd}</div>
                          <div className={styles.storyCycleLabel}>Süreç Bitiş Tarihi Eksik</div>
                        </div>
                        <div
                          className={`${styles.storyCycleTile} ${section1Summary.missedCount > 0 ? styles.issueClickable : ''}`}
                          onClick={section1Summary.missedCount > 0 ? () => openIssueModal('Kaçırılan Vizitler', s1Visits, 'missed') : undefined}
                          title={section1Summary.missedCount > 0 ? 'Detayları görüntüleyin' : undefined}
                        >
                          <div className={styles.storyCycleValue} style={{ color: section1Summary.missedCount > 0 ? '#c53030' : '#276749' }}>{section1Summary.missedCount}</div>
                          <div className={styles.storyCycleLabel}>Kaçırılan Vizit</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Monthly Trend & Pie Charts ── */}
                <div className={styles.capaChartGrid}>
                  {section1Summary.monthlyTrend.length > 1 && (
                    <div className={styles.capaChartCard}>
                      <h4 className={styles.capaChartTitle}>Aylık Vizit Trendi</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={section1Summary.monthlyTrend} margin={{ bottom: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="Ev" stackId="visits" fill="#4299e1" radius={[0, 0, 0, 0]} />
                          <Bar dataKey="Hastane" stackId="visits" fill="#9f7aea" radius={[4, 4, 0, 0]} />
                          <Line type="monotone" dataKey="Sorun" stroke="#fc8181" strokeWidth={2} dot={{ r: 4 }} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}

                  {capaDashboard && capaDashboard.pieChartData.length > 0 && (
                    <div className={styles.capaChartCard}>
                      <h4 className={styles.capaChartTitle}>Vizit Kalite Dağılımı</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie data={capaDashboard.pieChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} innerRadius={50} label={({ name, value }) => `${name}: ${value}`}>
                            {capaDashboard.pieChartData.map((entry, i) => {
                              const colorMap = { 'Sorunlu Vizit': '#fc8181', 'Sorunsuz Vizit': '#68d391' };
                              return <Cell key={i} fill={colorMap[entry.name] || '#a0aec0'} />;
                            })}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                </div>

                {/* ── Per-Coordinator Breakdown ── */}
                {capaDashboard && capaDashboard.coordinators.length > 0 && (
                  <div className={styles.capaMatrixContainer}>
                    <h3 className={styles.capaMatrixTitle}>Koordinatör Bazlı Ev Viziti Dağılımı</h3>
                    <div className={styles.capaMatrixScroll}>
                      <table className={styles.capaMatrixTable}>
                        <thead>
                          <tr>
                            <th className={styles.capaMatrixHeaderCell}>Koordinatör</th>
                            <th className={styles.capaMatrixHeaderCell}>Ev Viziti</th>
                            <th className={styles.capaMatrixHeaderCell}>Sorun</th>
                            <th className={styles.capaMatrixHeaderCell}>Kaçırılan</th>
                            <th className={styles.capaMatrixHeaderCell}>AE/SAE</th>
                            <th className={styles.capaMatrixHeaderCell}>DCF</th>
                            <th className={styles.capaMatrixHeaderCell}>Sorun Oranı</th>
                          </tr>
                        </thead>
                        <tbody>
                          {capaDashboard.coordinators.map(c => {
                            const issueRate = c.totalVisits > 0 ? Math.round(c.totalIssues / c.totalVisits * 100) : 0;
                            return (
                              <tr key={c.id} className={styles.capaMatrixRow}>
                                <td className={styles.capaMatrixCell} style={{ fontWeight: 600 }}>{c.name}</td>
                                <td className={styles.capaMatrixCell}>{c.totalVisits}</td>
                                <td className={`${styles.capaMatrixCell} ${c.totalIssues > 0 ? styles.capaMatrixClickable : ''}`} style={{ color: c.totalIssues > 0 ? '#c05621' : undefined }} onClick={c.totalIssues > 0 ? () => openIssueModal(`${c.name} — Sorunlar`, c.visits, 'issues') : undefined}>{c.totalIssues}</td>
                                <td className={`${styles.capaMatrixCell} ${c.missedVisits > 0 ? styles.capaMatrixClickable : ''}`} style={{ color: c.missedVisits > 0 ? '#c53030' : undefined, fontWeight: c.missedVisits > 0 ? 700 : 400 }} onClick={c.missedVisits > 0 ? () => openIssueModal(`${c.name} — Kaçırılan`, c.visits, 'missed') : undefined}>{c.missedVisits}</td>
                                <td className={`${styles.capaMatrixCell} ${c.aeSaeCount > 0 ? styles.capaMatrixClickable : ''}`} onClick={c.aeSaeCount > 0 ? () => openIssueModal(`${c.name} — AE/SAE`, c.visits, 'aesae') : undefined}>{c.aeSaeCount}</td>
                                <td className={`${styles.capaMatrixCell} ${c.dcfCreatedCount > 0 ? styles.capaMatrixClickable : ''}`} onClick={c.dcfCreatedCount > 0 ? () => openIssueModal(`${c.name} — DCF`, c.visits, 'dcf') : undefined}>{c.dcfCreatedCount}</td>
                                <td className={styles.capaMatrixCell}>
                                  <span className={styles.capaClosureRateBadge} style={{ background: issueRate <= 10 ? '#c6f6d5' : issueRate <= 30 ? '#fefcbf' : '#fed7d7', color: issueRate <= 10 ? '#22543d' : issueRate <= 30 ? '#744210' : '#9b2c2c' }}>%{issueRate}</span>
                                </td>
                              </tr>
                            );
                          })}
                          <tr className={`${styles.capaMatrixRow} ${styles.capaMatrixTotalRow}`}>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700 }}>TOPLAM</td>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700 }}>{capaDashboard.totals.totalVisits}</td>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700, color: '#c05621' }}>{capaDashboard.totals.totalIssues}</td>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700, color: '#c53030' }}>{capaDashboard.totals.missedVisits}</td>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700 }}>{capaDashboard.totals.aeSaeCount}</td>
                            <td className={styles.capaMatrixCell} style={{ fontWeight: 700 }}>{capaDashboard.totals.dcfCreatedCount}</td>
                            <td className={styles.capaMatrixCell}>
                              <span className={styles.capaClosureRateBadge} style={{
                                background: capaDashboard.totals.totalVisits > 0 ? (Math.round(capaDashboard.totals.totalIssues / capaDashboard.totals.totalVisits * 100) <= 10 ? '#c6f6d5' : '#fefcbf') : '#edf2f7',
                                color: capaDashboard.totals.totalVisits > 0 ? (Math.round(capaDashboard.totals.totalIssues / capaDashboard.totals.totalVisits * 100) <= 10 ? '#22543d' : '#744210') : '#a0aec0',
                                fontWeight: 700
                              }}>%{capaDashboard.totals.totalVisits > 0 ? Math.round(capaDashboard.totals.totalIssues / capaDashboard.totals.totalVisits * 100) : 0}</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SECTION 2: Kalite & Klinik Risk Paneli
            Story: "What quality or compliance risks need attention?"
            ══════════════════════════════════════════════════════════════ */}
        {selectedSMO && !loadingVisits && (
          <div className={styles.hastaneKpiSection}>
            <div className={styles.smoSectionTitle}>
              <FaExclamationTriangle style={{ marginRight: '0.5rem' }} />
              Kalite & Klinik Risk Paneli
              {section2Summary && <span className={styles.capaDashboardSubtitle}>{section2Summary.total} vizit</span>}
              {section2Summary && (
                <button className={styles.sectionDownloadButton} onClick={downloadHastaneKpiExcel} title="Excel olarak indir">
                  <FaDownload /> Excel
                </button>
              )}
              <button className={styles.sectionToggleButton} onClick={() => toggleSection('hastaneKpi')} title={expandedSections.hastaneKpi ? 'Daralt' : 'Genişlet'}>
                {expandedSections.hastaneKpi ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            {/* Date Range Filter */}
            <div className={styles.dateFilterRow}>
              <label className={styles.dateFilterLabel}>Tarih Aralığı:</label>
              <input type="date" className={styles.dateFilterInput} value={s2DateStart} onChange={e => setS2DateStart(e.target.value)} />
              <span className={styles.dateFilterSep}>—</span>
              <input type="date" className={styles.dateFilterInput} value={s2DateEnd} onChange={e => setS2DateEnd(e.target.value)} />
              <button className={styles.dateFilterBtn} onClick={() => resetDateFilter(2)} title="Bu ay">Bu Ay</button>
              <button className={styles.dateFilterBtn} onClick={() => setAllTime(2)} title="Tüm zamanlar">Tümü</button>
            </div>

            {!section2Summary && (
              <div className={styles.emptyState}>
                <FaExclamationTriangle className={styles.emptyStateIcon} style={{ color: '#a0aec0' }} />
                <p className={styles.emptyStateText}>Seçilen tarih aralığında vizit bulunamadı.</p>
              </div>
            )}

            {section2Summary && expandedSections.hastaneKpi && (
            <>
              {/* ── Quality Metric Tiles (clickable when count > 0) ── */}
              <div className={styles.storyQualityGrid}>
                {section2Summary.qualityTiles.map((tile, i) => {
                  const clickable = tile.count > 0;
                  return (
                    <div
                      key={i}
                      className={`${styles.storyQualityTile} ${clickable ? styles.issueClickable : ''}`}
                      onClick={clickable ? () => openIssueModal(`${tile.label} — ${section2Summary.total} vizit içinde`, s2Visits, tile.metric) : undefined}
                      title={clickable ? 'Detayları görüntülemek için tıklayın' : undefined}
                    >
                      <div className={styles.storyQualityCount} style={{ color: tile.color }}>{tile.count}</div>
                      <div className={styles.storyQualityLabel}>{tile.label}</div>
                      {tile.rate !== null && <div className={styles.storyQualityRate}>%{tile.rate}</div>}
                    </div>
                  );
                })}
              </div>

              {/* ── Risk Summary Table ── */}
              <div className={styles.hastaneKpiGrid}>
                <div className={styles.hastaneKpiCard}>
                  <h4 className={styles.hastaneKpiCardTitle}>Hasta Güvenliği & Uyumluluk</h4>
                  <table className={styles.hastaneKpiTable}>
                    <tbody>
                      {[
                        { label: 'AE/SAE Rapor Edilen', metric: 'aesae', count: section2Summary.aeSaeCount, color: section2Summary.aeSaeCount > 0 ? '#9b2c2c' : '#276749', text: `${section2Summary.aeSaeCount}` },
                        { label: 'DCF Oluşturulan', metric: 'dcf', count: section2Summary.dcfCount, color: undefined, text: `${section2Summary.dcfCount}` },
                        { label: 'Protokol Sapması', metric: 'deviation', count: section2Summary.deviationCount, color: section2Summary.deviationCount > 0 ? '#c53030' : undefined, text: `${section2Summary.deviationCount} (%${section2Summary.total > 0 ? Math.round(section2Summary.deviationCount / section2Summary.total * 100) : 0})` },
                        { label: 'CAPA Açılan', metric: 'capa', count: section2Summary.capaCount, color: section2Summary.capaCount > 0 ? '#c05621' : undefined, text: `${section2Summary.capaCount}` },
                        { label: 'Kaçırılan Vizit', metric: 'missed', count: section2Summary.missedCount, color: section2Summary.missedCount > 0 ? '#c53030' : undefined, text: `${section2Summary.missedCount}` },
                      ].map((row, i) => {
                        const clickable = row.count > 0;
                        return (
                          <tr
                            key={i}
                            className={clickable ? styles.issueClickableRow : undefined}
                            onClick={clickable ? () => openIssueModal(`${row.label} — ${section2Summary.total} vizit içinde`, s2Visits, row.metric) : undefined}
                            title={clickable ? 'Detayları görüntülemek için tıklayın' : undefined}
                          >
                            <td className={styles.hastaneKpiLabel}>{row.label}</td>
                            <td className={styles.hastaneKpiValue} style={{ color: row.color }}>{row.text}{clickable && <FaSearch style={{ marginLeft: 6, fontSize: '0.7rem', opacity: 0.6 }} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className={styles.hastaneKpiCard}>
                  <h4 className={styles.hastaneKpiCardTitle}>Hastane Vizitleri — Hasta Durumu</h4>
                  <table className={styles.hastaneKpiTable}>
                    <tbody>
                      <tr>
                        <td className={styles.hastaneKpiLabel}>Toplam Hastane Viziti</td>
                        <td className={styles.hastaneKpiValue}>{section2Summary.siteCount}</td>
                      </tr>
                      {[
                        { label: 'Tarama Başarısız (Screen Fail)', metric: 'screenFail', count: section2Summary.screenFail, color: section2Summary.screenFail > 0 ? '#c05621' : undefined, text: `${section2Summary.screenFail} ${section2Summary.siteCount > 0 ? `(%${Math.round(section2Summary.screenFail / section2Summary.siteCount * 100)})` : ''}` },
                        { label: 'Hasta Çıkışı (Drop Patient)', metric: 'dropPatient', count: section2Summary.dropPatient, color: section2Summary.dropPatient > 0 ? '#c05621' : undefined, text: `${section2Summary.dropPatient} ${section2Summary.siteCount > 0 ? `(%${Math.round(section2Summary.dropPatient / section2Summary.siteCount * 100)})` : ''}` },
                        { label: 'SLA Gecikmiş', metric: 'overdue', count: section2Summary.overdueCount, color: section2Summary.overdueCount > 0 ? '#c53030' : '#276749', text: `${section2Summary.overdueCount}` },
                        { label: 'Süreç Bitiş Tarihi Eksik', metric: 'missingProcessEnd', count: section2Summary.missingProcessEnd, color: section2Summary.missingProcessEnd > 0 ? '#c05621' : undefined, text: `${section2Summary.missingProcessEnd}` },
                      ].map((row, i) => {
                        const clickable = row.count > 0;
                        return (
                          <tr
                            key={i}
                            className={clickable ? styles.issueClickableRow : undefined}
                            onClick={clickable ? () => openIssueModal(`${row.label} — ${section2Summary.total} vizit içinde`, s2Visits, row.metric) : undefined}
                            title={clickable ? 'Detayları görüntülemek için tıklayın' : undefined}
                          >
                            <td className={styles.hastaneKpiLabel}>{row.label}</td>
                            <td className={styles.hastaneKpiValue} style={{ color: row.color }}>{row.text}{clickable && <FaSearch style={{ marginLeft: 6, fontSize: '0.7rem', opacity: 0.6 }} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Issue Breakdown Charts ── */}
              <div className={styles.capaChartGrid}>
                {capaDashboard && capaDashboard.barChartData.length > 0 && (
                  <div className={styles.capaChartCard}>
                    <h4 className={styles.capaChartTitle}>Koordinatör Bazlı Sorun Dağılımı</h4>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={capaDashboard.barChartData} margin={{ bottom: 40 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" />
                        <YAxis allowDecimals={false} />
                        <Tooltip labelFormatter={(label) => { const item = capaDashboard.barChartData.find(d => d.name === label); return item?.fullName || label; }} />
                        <Legend />
                        <Bar dataKey="Sorun" fill="#f6ad55" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Kaçırılan" fill="#fc8181" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {capaDashboard && capaDashboard.trendData.length > 1 && (
                  <div className={styles.capaChartCard}>
                    <h4 className={styles.capaChartTitle}>Aylık Sorun Trendi</h4>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={capaDashboard.trendData} margin={{ bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Sorun" stroke="#f6ad55" strokeWidth={2} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="Kaçırılan" stroke="#fc8181" strokeWidth={2} dot={{ r: 4 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SECTION 3: Ekip Performans Analizi
            Story: "How is each team member performing?"
            ══════════════════════════════════════════════════════════════ */}
        {selectedSMO && !loadingVisits && (
          <div className={styles.empDashSection}>
            <div className={styles.smoSectionTitle}>
              <FaUsers style={{ marginRight: '0.5rem' }} />
              Ekip Performans Analizi
              {employeeStats && <span className={styles.capaDashboardSubtitle}>{employeeStats.stats.length} çalışan analiz edildi</span>}
              {employeeStats && (
                <button className={styles.sectionDownloadButton} onClick={downloadEmployeeStatsExcel} title="Excel olarak indir">
                  <FaDownload /> Excel
                </button>
              )}
              <button className={styles.sectionToggleButton} onClick={() => toggleSection('employeePerf')} title={expandedSections.employeePerf ? 'Daralt' : 'Genişlet'}>
                {expandedSections.employeePerf ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            {/* Date Range Filter */}
            <div className={styles.dateFilterRow}>
              <label className={styles.dateFilterLabel}>Tarih Aralığı:</label>
              <input type="date" className={styles.dateFilterInput} value={s3DateStart} onChange={e => setS3DateStart(e.target.value)} />
              <span className={styles.dateFilterSep}>—</span>
              <input type="date" className={styles.dateFilterInput} value={s3DateEnd} onChange={e => setS3DateEnd(e.target.value)} />
              <button className={styles.dateFilterBtn} onClick={() => resetDateFilter(3)} title="Bu ay">Bu Ay</button>
              <button className={styles.dateFilterBtn} onClick={() => setAllTime(3)} title="Tüm zamanlar">Tümü</button>
            </div>

            {!employeeStats && (
              <div className={styles.emptyState}>
                <FaExclamationTriangle className={styles.emptyStateIcon} style={{ color: '#a0aec0' }} />
                <p className={styles.emptyStateText}>Seçilen tarih aralığında vizit bulunamadı.</p>
              </div>
            )}

            {employeeStats && expandedSections.employeePerf && (
            <>
              {/* ── Simplified Employee Table ── */}
              <div className={styles.empTableContainer}>
                <div className={styles.empTableScroll}>
                  <table className={styles.empTable}>
                    <thead>
                      <tr>
                        <th className={styles.empTableHeaderCell}>Saha Koordinatörü</th>
                        <th className={styles.empTableHeaderCell}>Vizit (Ev/Hastane)</th>
                        <th className={styles.empTableHeaderCell}>SLA %</th>
                        <th className={styles.empTableHeaderCell}>eCRF %</th>
                        <th className={styles.empTableHeaderCell}>Kaynak %</th>
                        <th className={styles.empTableHeaderCell}>Sorun / Sapma</th>
                        <th className={styles.empTableHeaderCell}>KM</th>
                        <th className={styles.empTableHeaderCell}>Gecikme</th>
                        <th className={styles.empTableHeaderCell}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...employeeStats.stats].sort((a, b) => b.totalVisits - a.totalVisits).map(emp => {
                        const empVisits = s3Visits.filter(v => v.employeeId === emp.id);
                        const empIssueCount = empVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes').length;
                        const issueOrDeviation = empVisits.filter(v => v.visitEvaluation?.issueOccurred === 'yes' || v.visitEvaluation?.protocolDeviation === 'yes');
                        return (
                        <tr key={emp.id} className={styles.empTableRow}>
                          <td className={styles.empTableCell} style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{emp.name}</td>
                          <td className={styles.empTableCell}>{emp.totalVisits} <span style={{ color: '#718096', fontSize: '0.75rem' }}>({emp.homeVisits}/{emp.siteVisits})</span></td>
                          <td className={styles.empTableCell} style={{ color: emp.slaMetPct < 80 ? '#c53030' : '#276749', fontWeight: 600 }}>{emp.slaMetPct}%</td>
                          <td className={styles.empTableCell}>{emp.ecrfPct}%</td>
                          <td className={styles.empTableCell}>{emp.sourcePct}%</td>
                          <td
                            className={`${styles.empTableCell} ${issueOrDeviation.length > 0 ? styles.issueClickableCell : ''}`}
                            onClick={issueOrDeviation.length > 0 ? () => openIssueModal(`${emp.name} — Sorun & Sapma`, issueOrDeviation) : undefined}
                            title={issueOrDeviation.length > 0 ? 'Detayları görüntülemek için tıklayın' : undefined}
                          >
                            <span style={{ color: (emp.deviationCount + empIssueCount) > 0 ? '#c05621' : '#276749' }}>
                              {empIssueCount} / {emp.deviationCount}
                            </span>
                          </td>
                          <td className={styles.empTableCell}>{emp.totalKm}</td>
                          <td
                            className={`${styles.empTableCell} ${emp.overdueCount > 0 ? styles.issueClickableCell : ''}`}
                            style={{ color: emp.overdueCount > 0 ? '#c53030' : undefined, fontWeight: emp.overdueCount > 0 ? 700 : 400 }}
                            onClick={emp.overdueCount > 0 ? () => openIssueModal(`${emp.name} — Gecikmiş Vizitler`, empVisits, 'overdue') : undefined}
                            title={emp.overdueCount > 0 ? 'Detayları görüntülemek için tıklayın' : undefined}
                          >{emp.overdueCount}</td>
                          <td className={styles.empTableCell}>
                            <button
                              className={styles.empStatsButton}
                              onClick={() => navigate('/employee-statistics', {
                                state: {
                                  employee: {
                                    id: emp.id,
                                    name: emp.name
                                  },
                                  employeeStats: emp, // Pass the entire employee stats object
                                  visits: allVisits.filter(v => v.employeeId === emp.id), // Pass filtered visits
                                  dateRange: {
                                    start: s3DateStart,
                                    end: s3DateEnd
                                  }
                                }
                              })}
                              title="Detaylı İstatistikler"
                            >
                              <FaChartLine /> İstatistikler
                            </button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Charts ── */}
              <div className={styles.empChartGrid}>
                {/* Workload Distribution: Stacked Home/Site visits per employee */}
                {employeeStats.stats.length > 0 && (
                  <div className={styles.empChartCard}>
                    <h4 className={styles.empChartTitle}>İş Yükü Dağılımı (Vizit Sayısı)</h4>
                    <ResponsiveContainer width="100%" height={Math.max(280, employeeStats.stats.length * 36)}>
                      <BarChart
                        data={[...employeeStats.stats].sort((a, b) => b.totalVisits - a.totalVisits).slice(0, 10).map(s => ({
                          name: s.name.length > 18 ? s.name.substring(0, 17) + '…' : s.name,
                          fullName: s.name,
                          'Ev Viziti': s.homeVisits,
                          'Hastane Viziti': s.siteVisits,
                        }))}
                        layout="vertical"
                        margin={{ left: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip labelFormatter={l => { const d = employeeStats.stats.find(x => (x.name.length > 18 ? x.name.substring(0, 17) + '…' : x.name) === l); return d?.name || l; }} />
                        <Legend />
                        <Bar dataKey="Ev Viziti" stackId="visits" fill="#4299e1" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="Hastane Viziti" stackId="visits" fill="#9f7aea" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* KM per Employee */}
                {employeeStats.kmPerNurse.length > 0 && (
                  <div className={styles.empChartCard}>
                    <h4 className={styles.empChartTitle}>Çalışan Başına KM</h4>
                    <ResponsiveContainer width="100%" height={Math.max(280, employeeStats.kmPerNurse.length * 36)}>
                      <BarChart data={employeeStats.kmPerNurse} layout="vertical" margin={{ left: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={120} />
                        <Tooltip labelFormatter={l => { const d = employeeStats.kmPerNurse.find(x => x.name === l); return d?.fullName || l; }} />
                        <Bar dataKey="KM" name="KM" radius={[0, 4, 4, 0]}>
                          {employeeStats.kmPerNurse.map((_, i) => {
                            const colors = ['#4299e1','#fc8181','#68d391','#9f7aea','#4fd1c5','#f6ad55','#4299e1','#fc8181','#68d391','#9f7aea'];
                            return <Cell key={i} fill={colors[i % colors.length]} />;
                          })}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* SLA: Home vs Site */}
                <div className={styles.empChartCard}>
                  <h4 className={styles.empChartTitle}>Ev vs Hastane: SLA Karşılanma</h4>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={employeeStats.homeVsSiteSla}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis unit="%" domain={[0, 100]} />
                      <Tooltip formatter={v => v + '%'} />
                      <Legend />
                      <Bar dataKey="SLA Karşılanma %" radius={[4, 4, 0, 0]}>
                        {employeeStats.homeVsSiteSla.map((_, i) => (
                          <Cell key={i} fill={i === 0 ? '#4299e1' : '#9f7aea'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
            )}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SECTION 4: Ekip Sistem Kullanımı
            Story: "Who is online right now, and how much time does each
            team member actually spend in the system?"
            ══════════════════════════════════════════════════════════════ */}
        {selectedSMO && (
          <div className={styles.empDashSection}>
            <div className={styles.smoSectionTitle}>
              <FaUserClock style={{ marginRight: '0.5rem' }} />
              Ekip Sistem Kullanımı
              <span className={styles.capaDashboardSubtitle}>
                <FaCircle
                  style={{
                    fontSize: '0.55rem',
                    marginRight: '0.35rem',
                    color: teamActivity.summary.onlineCount > 0 ? '#48bb78' : '#a0aec0',
                  }}
                />
                {teamActivity.summary.onlineCount} / {teamActivity.summary.teamSize} çevrimiçi
              </span>
              <button
                className={styles.sectionToggleButton}
                onClick={() => toggleSection('teamActivity')}
                title={expandedSections.teamActivity ? 'Daralt' : 'Genişlet'}
                aria-expanded={expandedSections.teamActivity}
              >
                {expandedSections.teamActivity ? <FaChevronUp /> : <FaChevronDown />}
              </button>
            </div>

            {/* Date Range Filter */}
            <div className={styles.dateFilterRow}>
              <label className={styles.dateFilterLabel}>Tarih Aralığı:</label>
              <input type="date" className={styles.dateFilterInput} value={s4DateStart} onChange={e => setS4DateStart(e.target.value)} />
              <span className={styles.dateFilterSep}>—</span>
              <input type="date" className={styles.dateFilterInput} value={s4DateEnd} onChange={e => setS4DateEnd(e.target.value)} />
              <button className={styles.dateFilterBtn} onClick={() => resetDateFilter(4)} title="Bu ay">Bu Ay</button>
              <button className={styles.dateFilterBtn} onClick={() => setAllTime(4)} title="Tüm kayıtlı dönem">Tümü</button>
            </div>

            {expandedSections.teamActivity && (
              <TeamActivityPanel
                data={teamActivity}
                startDate={s4DateStart}
                endDate={s4DateEnd}
                smoName={selectedSMO.name ? `${selectedSMO.name} ${selectedSMO.surname || ''}`.trim() : ''}
              />
            )}
          </div>
        )}

        {/* Issue Detail Modal — drill-down for any clicked issue indicator */}
        {issueModal && (
          <IssueDetailModal
            show={!!issueModal}
            onHide={() => setIssueModal(null)}
            title={issueModal.title}
            visits={issueModal.visits}
            workers={workers}
            projectNames={projectNameMap}
            isDarkMode={isDarkMode}
          />
        )}

        {/* CV/GCP/IATA belgeleri — ekipteki çalışanın belgelerini görüntüle */}
        <EmployeeDocumentsModal
          show={!!documentsEmployee}
          onHide={() => setDocumentsEmployee(null)}
          employee={documentsEmployee}
          isDarkMode={isDarkMode}
        />

        {/* Visits Modal */}
        {selectedEmployee && (
          <ProjectVisitsModal
            show={showVisitsModal}
            onHide={() => {
              setShowVisitsModal(false);
              setSelectedEmployee(null);
            }}
            project={selectedEmployee.project}
            employeeId={selectedEmployee.id}
            employeeName={selectedEmployee.name}
            visits={selectedEmployee.visits}
          />
        )}

        {/* Manual Data Entry Modal */}
        {canManualEntry && showManualEntry && (
          <ManualDataEntryModal
            show={showManualEntry}
            onHide={() => setShowManualEntry(false)}
            workers={workers}
            selectedSMO={selectedSMO}
            onDataSaved={() => {
              // Re-fetch visits to refresh dashboards
              if (workers.length > 0) {
                const fetchVisits = async () => {
                  try {
                    const workerIds = workers.map(w => w.id);
                    const BATCH_SIZE = 30;
                    const batches = [];
                    for (let i = 0; i < workerIds.length; i += BATCH_SIZE) {
                      batches.push(workerIds.slice(i, i + BATCH_SIZE));
                    }
                    const results = await Promise.all(
                      batches.map(batch => {
                        const q = query(collection(db, 'Visits'), where('employeeId', 'in', batch));
                        return getDocs(q);
                      })
                    );
                    setAllVisits(results.flatMap(snap => snap.docs.map(d => ({ id: d.id, ...d.data() }))));
                  } catch (e) {
                    console.error('Error re-fetching visits:', e);
                  }
                };
                fetchVisits();
              }
            }}
          />
        )}

        {/* Incomplete Tasks Modal */}
        {showIncompleteTasksModal && (
          <div className={styles.modalOverlay}>
            <div className={`${styles.modal} ${styles.incompleteTasksModal} ${isDarkMode ? 'dark-mode' : ''}`}>
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>
                  <FaClipboardList style={{ marginRight: '0.5rem', color: '#3182ce' }} />
                  Tamamlanmamış Görevler
                </h3>
                <button 
                  className={styles.modalCloseButton}
                  onClick={() => setShowIncompleteTasksModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className={styles.modalBody}>
                {incompleteTasks.total === 0 ? (
                  <div className={styles.emptyState}>
                    <FaClipboardList className={styles.emptyStateIcon} style={{ color: '#68d391' }} />
                    <p className={styles.emptyStateText}>
                      Harika! Tüm görevler tamamlanmış durumda.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={styles.incompleteTasksHeader}>
                      <p className={styles.incompleteTasksCount}>
                        <strong>{incompleteTasks.total}</strong> tamamlanmamış görev bulundu.
                        ({incompleteTasks.visits.length} vizit, {incompleteTasks.jobs.length} takvim görevi)
                      </p>
                    </div>
                    
                    <div className={styles.incompleteTasksTable}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Tarih</th>
                            <th>Çalışan</th>
                            <th>Görev Tipi</th>
                            <th>Açıklama</th>
                            <th>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {incompleteTasks.all.map((task, index) => (
                            <tr key={task.id || index} className={styles.incompleteTaskRow}>
                              <td>{task.date ? new Date(task.date).toLocaleDateString('tr-TR') : '-'}</td>
                              <td>{task.workerName}</td>
                              <td>
                                <span className={`${styles.taskTypeBadge} ${task.type === 'visit' ? styles.taskTypeVisit : styles.taskTypeJob}`}>
                                  {task.type === 'visit' ? 'Vizit' : 'Takvim Görevi'}
                                </span>
                              </td>
                              <td>
                                {task.type === 'visit' ? (
                                  <div>
                                    <strong>{task.patientId || 'Hasta'}</strong>
                                    <br />
                                    <small>{task.visitType === 'home' ? 'Ev Viziti' : 'Hastane Viziti'}</small>
                                  </div>
                                ) : (
                                  <div>
                                    <strong>{task.title}</strong>
                                    {task.description && <><br /><small>{task.description}</small></>}
                                  </div>
                                )}
                              </td>
                              <td>
                                <span className={`${styles.statusBadge} ${styles.statusIncomplete}`}>
                                  Tamamlanmadı
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              
              <div className={styles.modalFooter}>
                <button 
                  className={styles.modalButton}
                  onClick={() => setShowIncompleteTasksModal(false)}
                >
                  Kapat
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Admin confirmation modal for removing a worker or an SMO */}
        {confirmAction && (
          <div
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 2000, padding: 16,
            }}
            onClick={() => !removing && setConfirmAction(null)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: isDarkMode ? '#1e293b' : '#ffffff',
                color: isDarkMode ? '#f1f5f9' : '#1e293b',
                borderRadius: 14, width: '100%', maxWidth: 460,
                boxShadow: '0 20px 50px rgba(15,23,42,0.4)', overflow: 'hidden',
              }}
            >
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 20px', borderBottom: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
              }}>
                <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center' }}>
                  <FaExclamationCircle style={{ marginRight: '0.5rem', color: '#e53e3e' }} />
                  {confirmAction.type === 'worker' ? 'Çalışanı Çıkar' : "SMO'yu Kaldır"}
                </h3>
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={removing}
                  style={{
                    background: 'none', border: 'none', cursor: removing ? 'not-allowed' : 'pointer',
                    color: isDarkMode ? '#94a3b8' : '#64748b', fontSize: '1rem', display: 'flex',
                  }}
                >
                  <FaTimes />
                </button>
              </div>

              <div style={{ padding: '20px', lineHeight: 1.6, fontSize: '0.92rem' }}>
                {confirmAction.type === 'worker' ? (
                  <p style={{ margin: 0 }}>
                    <strong>{confirmAction.worker.name} {confirmAction.worker.surname}</strong> adlı çalışanı{' '}
                    <strong>{selectedSMO?.name} {selectedSMO?.surname}</strong> SMO'sundan çıkarmak istediğinize emin misiniz?
                    <br /><br />
                    Bu işlem, çalışanın bu SMO'ya bağlı merkezlerdeki tüm proje atamalarını kaldırır ve geri alınamaz.
                  </p>
                ) : (
                  <p style={{ margin: 0 }}>
                    <strong>{selectedSMO?.name} {selectedSMO?.surname}</strong> SMO atamasını{' '}
                    <strong>{selectedSMO?.centerCount}</strong> merkezden kaldırmak istediğinize emin misiniz?
                    <br /><br />
                    Merkezler ve çalışan atamaları korunur; yalnızca bu kişinin SMO ataması temizlenir. SMO'yu daha sonra Merkez Yönetimi'nden tekrar atayabilirsiniz.
                  </p>
                )}
              </div>

              <div style={{
                display: 'flex', gap: 10, justifyContent: 'flex-end',
                padding: '14px 20px', borderTop: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
              }}>
                <button
                  onClick={() => setConfirmAction(null)}
                  disabled={removing}
                  style={{
                    padding: '9px 18px', borderRadius: 8, border: 'none', cursor: removing ? 'not-allowed' : 'pointer',
                    background: isDarkMode ? '#334155' : '#e2e8f0', color: isDarkMode ? '#e2e8f0' : '#475569',
                    fontWeight: 600, fontSize: '0.875rem',
                  }}
                >
                  İptal
                </button>
                <button
                  onClick={() => confirmAction.type === 'worker' ? removeWorkerFromSMO(confirmAction.worker) : removeSMO()}
                  disabled={removing}
                  style={{
                    padding: '9px 18px', borderRadius: 8, border: 'none', cursor: removing ? 'not-allowed' : 'pointer',
                    background: '#e53e3e', color: '#ffffff', fontWeight: 600, fontSize: '0.875rem',
                    display: 'inline-flex', alignItems: 'center', opacity: removing ? 0.7 : 1,
                  }}
                >
                  {removing ? 'İşleniyor...' : (
                    <>
                      <FaTrashAlt style={{ marginRight: '0.4rem' }} />
                      {confirmAction.type === 'worker' ? 'Çıkar' : 'Kaldır'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />
      </div>
    </div>
  );
};

export default SMOLeadReports;
